import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
// @rails/request.js, never native fetch: it carries the CSRF token and
// credentials for same-origin Rails endpoints (project rule).
import { post } from "@rails/request.js"

import { prefersReducedMotion } from "../../lib/prefers-reduced-motion"
import { renderMarkdown } from "../../lib/render-markdown"
import { createTextStream } from "../../lib/text-stream"

import { getConsumer } from "@shared/lib/action-cable/consumer"
import {
  highlightChanges,
  snapshotTexts,
} from "@shared/lib/companion-change-highlight"

// Markdown rendering (config + sanitisation) lives in
// ../../lib/render-markdown — links open in a new tab, disable Turbo
// navigation, and output is DOMPurify-sanitised before it ever reaches
// innerHTML.

// The assistant avatar (the `conek` icon), the suggestion chip and every piece
// of ai.companion_ux_refresh chrome are cloned from server-rendered <template>s
// (AI::Companion::TranscriptTemplatesComponent), so no markup, class list or
// brand colour lives in this file.

// GA programme F2.2 — `ui_component` broadcasts that are canvas instructions
// rather than cards. `packs/ai/app/agents/companion_agent/drawing_tools.rb`
// emits them with `data:` and deliberately no `html:` (CompanionCardRenderer
// has no component for them), so they must be bridged to window events instead
// of rendered into the transcript. Mirrors the constants that lived in
// use-ai-chat.ts before the React chrome was deleted; the listeners live in
// app/frontend/domains/orders/components/drawings/editor/ai-generation/
// use-ai-drawing-placement.ts.
// `_stoppedTurn` while Stop was pressed before the agent's first frame named
// the turn: the next agent turn to show up is the stopped one (CONEK-6946).
const STOP_PENDING = Symbol("stop-pending")

const CANVAS_BRIDGE_EVENTS = Object.freeze({
  drawing_placement: "ai:drawing-placement",
  drawing_zoom_to_entity: "ai:drawing-zoom-to-entity",
  drawing_change_combination: "ai:drawing-change-combination",
})

// Frames that prove a stalled turn is still alive (CONEK-7585): it produced
// output after the stall timer gave up on it, or it acked again — `thinking`
// and the channel's `turn_in_flight` replay each open a new stall window, so
// the old marker must go or a second stall leaves it orphaned above the answer.
const STALL_REVIVING_FRAMES = new Set([
  "response",
  "tool_call_start",
  "tool_call_end",
  "ui_component",
  "thinking",
  "turn_in_flight",
])

// "group-key:branchA,branchB" from a `/s:group-key:branchA,branchB/...` path,
// or undefined off a space-scoped URL (the server then falls back to the
// session's branches).
function currentSpaceScope() {
  const match = window.location.pathname.match(/^\/s:([^/]+)/)
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * AI Companion Controller — CONEK-3364 Wave 4.
 *
 * Owns the entire client-side state machine for the Hotwire companion
 * surface. Subscribes to AIChatChannel with `ai_chat_id`, dispatches the
 * 10 wire event types (per plan v7 W4.2). Generative-UI cards (`ui_component`,
 * `tool_result`) arrive pre-rendered as a `<turbo-stream action="append">`
 * HTML string — the server renders the real ViewComponent against the real
 * payload (AI::CompanionCardRenderer) and this controller just hands the
 * markup to `Turbo.renderStreamMessage()`. Workstream 3 sub-phase 1 —
 * docs/technical/architecture/hotwire-generative-ui.md — replaced the
 * client-side `companion-dsl.js` stamping engine, which pre-rendered each
 * card's `<template>` ONCE at page load (before any payload existed) and
 * substituted `{{path}}` placeholders at runtime: the root cause of
 * collection cards (search_results, options_list) always rendering their
 * empty branch.
 *
 * Wire event types (verified envelope shapes — plan v7 C1):
 *   connected      {type, ai_chat_id, name}
 *   history        {type, messages:[{id, role, content, context, created_at}]}
 *   tool_call_start {type, tool_name}
 *   thinking       {type}
 *   response       {type, content}      ← streaming token
 *   ui_component   {type, component, html, data} ← pre-rendered turbo-stream
 *                  append; `html` absent + a CANVAS_BRIDGE_EVENTS component
 *                  means it is a canvas instruction carried on `data`, not a card
 *   navigation     {type, url, entity_type, entity_id}
 *   tool_result    {type, html}         ← pre-rendered turbo-stream append
 *   done           {type}
 *   error          {type, message}
 *
 * State machine NOTES (simplified vs React useAiChat.ts:191-568):
 * - streamingMessageId: generated via crypto.randomUUID() on `thinking`
 *   arrival; used to thread `response` chunks into the same bubble.
 * - finalizeStreaming: called on `done`; clears streamingMessageId.
 * - ui_component/tool_result with no `html` (server-side render miss —
 *   unrecognised component or payload/signature mismatch, logged server-side
 *   by AI::CompanionCardRenderer) are silently skipped rather than crashing
 *   the transcript.
 *
 * Targets:
 *   - input, sendButton         (composer; managed by ComposerComponent)
 *   - messages                  (chat surface — turbo-stream appends land here)
 *   - status, error, loading    (transient UI banners)
 *   - contextChips, contextChipsList  (entity-chip strip above composer)
 *   - maximizeLink              (panel header's maximize <a>; href is kept
 *                                on the current thread — panel-only target)
 *
 * Values:
 *   - chatId               (drives subscription params + render scoping)
 *   - spaceGroupId         (drives getConsumer cache key — see V29)
 *   - notConnectedMessage  (i18n, workstream 3 sub-phase 2 reliability layer)
 *   - timedOutMessage      (i18n, workstream 3 sub-phase 2 reliability layer;
 *                           also the stalled assistant turn's note, CONEK-7585)
 *   - retryLabel           (i18n, workstream 3 sub-phase 2 reliability layer)
 *   - interruptedLabel     (i18n, CONEK-6946 — the "partial answer" note on a
 *                           stopped assistant turn)
 *   - connectedStatus      (i18n, workstream 3 sub-phase 5 — every mount of
 *                           this controller MUST set this or the status
 *                           pill silently renders blank)
 *   - reconnectingStatus   (i18n, workstream 3 sub-phase 5 — same as above)
 */
export default class extends Controller {
  static targets = [
    "input",
    "sendButton",
    "messages",
    "status",
    "error",
    "errorText",
    "loading",
    "contextChips",
    "contextChipsList",
    "emptyState",
    "suggestions",
    "clearButton",
    "clearDialog",
    "attachmentInput",
    "voiceButton",
    "composerStatus",
    "maximizeLink",
    // ai.companion_panel_v2 — server-rendered Ui::ToastComponent templates
    // (copy stays in Rails i18n, no design classes in JS).
    "refreshedToastTemplate",
    "staleToastTemplate",
    // ai.companion_answer_style — the strip above the textarea.
    "composerSuggestions",
    // ai.companion_panel_v2 — server-rendered icons for the pin control.
    "pinIconTemplate",
    "pinnedIconTemplate",
    // AI::Companion::TranscriptTemplatesComponent — avatar and starter chip
    // always; the rest only cloned under ai.companion_ux_refresh.
    "avatarTemplate",
    "suggestionChipTemplate",
    "stepsTemplate",
    "stepRowTemplate",
    "messageActionsTemplate",
    "inlineErrorTemplate",
    "jumpToLatest",
  ]

  static values = {
    chatId: String,
    spaceGroupId: String,
    playgroundMode: { type: Boolean, default: false },
    initialChatId: String,
    // Workstream 3 sub-phase 2 (reliability layer) — translated strings
    // rendered server-side (t()) and injected as values, since this
    // controller has no i18n runtime of its own.
    notConnectedMessage: String,
    timedOutMessage: String,
    // Last-resort banner text for an `error` frame that arrived with no
    // message at all. Server-rendered so it is translated; it replaced a
    // hardcoded English "Unexpected error" literal.
    genericErrorMessage: String,
    retryLabel: String,
    interruptedLabel: String,
    connectedStatus: String,
    reconnectingStatus: String,
    // Composer control strings — same server-rendered-t() pattern as above.
    voiceStartLabel: String,
    voiceStopLabel: String,
    voiceRecordingLabel: String,
    voiceTranscribingLabel: String,
    voiceFailedMessage: String,
    voiceUnsupportedMessage: String,
    voiceDeniedMessage: String,
    attachmentUploadingLabel: String,
    attachmentFailedMessage: String,
    attachmentRemoveLabel: String,
    // CONEK-7341 — template for the entity-context placeholder swap
    // ("Ask about %{entity_name}…"), rendered server-side via t() and
    // interpolated client-side, since the entity name only arrives with the
    // companion:context event after mount. Absent value = keep the default.
    askAboutTemplate: String,
    // Activity indicator labels ("Pensando…", "Buscando contactos…"). Absent
    // on the admin Playground mount, which keeps the raw tool name on purpose
    // (see #toolActivityLabel).
    thinkingLabel: String,
    toolActivityLabels: Object,
    toolActivityFallback: String,
    // ai.companion_panel_v2 — panel only. Off (absent) keeps the
    // context chips and the message payload exactly as they were.
    pinningEnabled: { type: Boolean, default: false },
    pinLabel: String,
    unpinLabel: String,
    transcriptionUrl: String,
    attachmentUrl: String,
    // Full-page shell only: the space-scoped chats index
    // (`/s:…/companion/chats`). When set, the address bar follows the selected
    // chat via history.replaceState (#syncChatUrl). The panel never sets it —
    // it rides on someone else's page and must not rewrite that page's URL.
    chatsPath: String,
    // ai.companion_ux_refresh — absent (false) keeps every behaviour below
    // exactly as it was. The labels are the past forms of toolActivityLabels;
    // stepsSummary holds every plural form of "Worked for … · N steps",
    // picked with Intl.PluralRules for `locale`.
    uxRefresh: { type: Boolean, default: false },
    toolActivityDoneLabels: Object,
    toolActivityDoneFallback: String,
    toolActivityFailed: String,
    stepsSummary: Object,
    locale: String,
  }

  // CONEK-2966-equivalent: if no `thinking` ack arrives within this window
  // after sending, the message is marked failed. If no `response` chunk
  // arrives within this window of the last one, the streaming bubble is
  // marked stalled. Mirrors AI_ACK_TIMEOUT_MS in the React
  // use-ai-chat.ts reference (10s, tuned against p99 first-chunk latency).
  static ACK_TIMEOUT_MS = 10_000

  // The window from `thinking` until the turn produces its FIRST output —
  // which may be a `response` chunk OR a `tool_call_start`.
  //
  // This is deliberately much larger than ACK_TIMEOUT_MS. That 10s is tuned
  // against first-CHUNK latency for a prose answer; a turn that opens by
  // calling a tool instead spends its first round deciding, with nothing to
  // stream. `onToolCallStart` already documents measured gaps of 5s to 18s
  // for that, and fixed the window AFTER a tool call — but the window BEFORE
  // the first one was still ACK_TIMEOUT_MS, so any tool-opening turn slower
  // than 10s rendered a Retry button over a perfectly healthy turn (observed:
  // `thinking` at T+0.0s, first `tool_call_start` at T+12.6s, stall fired at
  // T+10.0s).
  //
  // Sized under the server's own ceiling so the server is what fails first:
  // ruby_llm's request_timeout is 90s (config/initializers/ruby_llm.rb), and
  // a hard failure there arrives as an `error` frame, which is a better
  // message than a speculative client-side stall.
  static FIRST_OUTPUT_TIMEOUT_MS = 45_000

  // A bulk write or a write followed by its narration can land several
  // `record_changed` frames back to back; one refresh covers them all.
  static RECORD_CHANGED_COALESCE_MS = 400

  connect() {
    this.streamingMessageId = null
    this.thinkingStartedAt = null
    this.isConnected = false
    // Last ai_chat_id the channel rejected. Bounds the draft-fallback retry in
    // #handleSubscriptionRejected to one attempt per id.
    this.rejectedChatId = null
    // Composer control state. Deliberately instance state, not Stimulus
    // values: none of it should survive a reconnect, a half-uploaded image or
    // an abandoned recording must not reappear.
    this.pendingAttachments = []
    // Bumped by #switchChat. An upload remembers the value it started under so
    // a response that lands after the user left that thread can be dropped.
    this.threadEpoch = 0
    this.mediaRecorder = null
    // Streaming render state — one entry per in-flight assistant bubble:
    // { bubble, raw, handle, finalized }. `raw` accumulates the exact chunk
    // text (source of truth for the final markdown parse); `handle` is the
    // shared text-stream engine that paces the visual reveal.
    this._stream = null
    // Out-of-order frame guard. ActionCable's worker pool may transmit a
    // burst of broadcasts out of publish order (each frame is posted to a
    // thread pool). The server stamps `seq` on companion frames; we hold
    // early arrivals in _seqPending until the gap fills (or a short safety
    // timer flushes them in order). Frames without `seq` bypass the gate.
    this._seqTurn = null
    this._seqExpected = null
    this._seqPending = new Map()
    this._seqFlushTimer = null
    // {seq, content} of every `response` chunk dispatched for the current
    // turn — what onTurnInFlight needs to merge a snapshot with live frames
    // that raced ahead of it. Cleared with the seq window.
    this._turnChunks = []
    // The running round's text that Clear wiped from view. The server
    // snapshot a reconnect replays (turn_in_flight) still opens with it, so
    // onTurnInFlight strips it — but only when the snapshot really starts
    // with it: the snapshot restarts with each tool round, and a blip can
    // swallow the tool_call_start that would have reset this. Reset on
    // tool_call_start and with the seq window.
    this._clearedText = ""
    // CONEK-6946 — the agent turn the user pressed Stop on (or STOP_PENDING
    // when that turn had not named itself yet). Its late frames — chunks that
    // were already on the wire, a card from a tool that was still running —
    // are dropped until its `done`, instead of opening an orphan bubble under
    // the interrupted one. `_awaitingTurn` is true between a send and the
    // agent's first turn-stamped `thinking`, i.e. while `_seqTurn` still
    // names the PREVIOUS turn.
    this._stoppedTurn = null
    this._awaitingTurn = false
    // messageId -> timeoutId. One ack timer per outgoing user message
    // (only ever one in flight in practice, but keyed defensively) and one
    // stall timer per streaming assistant bubble.
    this.ackTimeouts = new Map()
    this.stallTimeouts = new Map()
    // CONEK-7585 — the bubble the stall timer marked, while the turn may still
    // answer, and that turn's id (null until a turn-stamped frame names it).
    // Only that turn's frames settle the marker (#settleStalledTurn); sending a
    // new message or stopping that turn makes it final, and replacing the
    // transcript drops it.
    this._stalledBubble = null
    this._stalledTurn = null
    this.lastSentUserMessageId = null
    // ai.companion_ux_refresh — the steps line of the turn in flight
    // (#startStep / #endStep / #finishSteps) and the raw markdown of every
    // finished assistant bubble, which Copy hands to the clipboard.
    this._steps = null
    this._rawByBubble = new WeakMap()
    // Whether the transcript follows new content. Measured from what the USER
    // does (#syncJumpToLatest), never from the distance after content grew: a
    // tall card landing under a reader who was at the bottom must still scroll.
    this._pinned = true
    this._lastScrollTop = 0
    if (this.uxRefreshValue && this.hasMessagesTarget) {
      this._handleMessagesScroll = () => this.#syncJumpToLatest()
      // The wheel says "up" before the scroll lands, so the reveal's next tick
      // can no longer win the race and snap the reader back down.
      this._handleMessagesWheel = (event) => {
        if (event.deltaY < 0) this._pinned = false
      }
      this.messagesTarget.addEventListener(
        "scroll",
        this._handleMessagesScroll,
        { passive: true },
      )
      this.messagesTarget.addEventListener("wheel", this._handleMessagesWheel, {
        passive: true,
      })
    }
    this.defaultPlaceholder = this.hasInputTarget
      ? this.inputTarget.getAttribute("placeholder")
      : null
    // GA programme F2 — snapshot the server-rendered generic suggestion chips
    // so handleCompanionContextCleared can restore them after a page-specific
    // set has replaced them. Captured here (not re-derived) because the
    // fallback text is i18n'd server-side and this controller has no i18n
    // runtime of its own.
    this.defaultSuggestionsHtml = this.hasSuggestionsTarget
      ? this.suggestionsTarget.innerHTML
      : null

    this.#subscribe(this.chatIdValue || this.initialChatIdValue || null)
    // Empty state / Clear button start correct (workstream 3 sub-phase 3) —
    // otherwise both stay in their server-rendered "hidden" state forever
    // until the first bubble renders (and immediately un-hides itself).
    this.#syncTranscriptState()
    this.#armSkeletonTimeout()

    // CONEK-3366 — Auto-pickup entity context broadcast from per-page
    // ai--companion-context controllers (Quote, SupportTicket, …).
    this._handleCompanionContext = (event) => this.handleCompanionContext(event)
    this._handleCompanionContextCleared = () =>
      this.handleCompanionContextCleared()
    window.addEventListener("companion:context", this._handleCompanionContext)
    window.addEventListener(
      "companion:context-cleared",
      this._handleCompanionContextCleared,
    )

    // The Hotwire chat-list rail refreshes itself via a turbo-frame, so
    // selecting a chat / starting a new one no longer reloads the page.
    // Listen for the rail's dispatched events and re-point the live
    // AIChatChannel subscription at the chosen thread — this is what
    // loads the selected chat's message history into the pane (the
    // server replays it on subscribe). Without this, the messages pane
    // stayed wired to whatever chat was active on page load.
    this._handleRailSelectChat = (event) =>
      this.#switchChat(event.detail?.chatId ?? null)
    this._handleRailNewChat = () => {
      // "New chat" from the rail means the default companion, not whatever
      // persona was picked for the previous draft.
      this.pendingAgentId = null
      this.#switchChat(null)
    }
    this.element.addEventListener(
      "ai--chat-list-rail:select-chat",
      this._handleRailSelectChat,
    )
    this.element.addEventListener(
      "ai--chat-list-rail:new-chat",
      this._handleRailNewChat,
    )

    // Managed-agent persona picker (rendered in the empty state). The picker
    // dispatches on `window`, not on this element. Binding is decided when
    // the chat is created, so picking an agent always starts a fresh draft:
    // we stash the id and force a re-subscribe, which sends `ai_agent_id` as
    // a subscription param for the channel's lazy chat creation.
    this._handlePickAgent = (event) => this.#handlePickAgent(event)
    window.addEventListener("companion:pick-agent", this._handlePickAgent)
  }

  #handlePickAgent(event) {
    this.pendingAgentId = event.detail?.agentId ?? null
    // Always re-subscribe, even when already on a draft: #switchChat(null)
    // early-returns only for a same non-null id, so this reliably re-sends
    // the subscription with the new agent param.
    this.#switchChat(null)
  }

  // Create (or recreate) the AIChatChannel subscription for a given
  // chat id. Centralised so connect() and chat-switching share one path.
  // Every action frame carries the space scope of THIS tab's URL. The socket
  // outlives Turbo and Inertia navigations (neither reconnects the cable), so
  // the server cannot learn a branch switch from the connection; it re-checks
  // the per-branch `ai.enabled` entitlement, and picks the branch write tools
  // land in, from this value on every frame (AIChatChannel#current_branch_ids).
  #perform(action, payload = {}) {
    this.subscription?.perform(action, {
      ...payload,
      space_scope: currentSpaceScope(),
    })
  }

  #subscribe(chatId) {
    // Idempotent on purpose. Assigning over a live `this.subscription` loses
    // the only reference to it while ActionCable keeps it SUBSCRIBED, so the
    // orphan goes on delivering: every frame then arrives twice and can never
    // be cleaned up. `connect()` can fire more than once for the same element
    // under Turbo without an intervening `disconnect()`, which is how the
    // duplicate showed up in practice (one "Broadcasting" line, two
    // "transmitting" lines with an identical seq).
    //
    // The `seq` reorder gate now drops the duplicate text, so this is no
    // longer visible in the answer itself, but the duplicate delivery is still
    // wasted work and still doubles frames the gate does not key on.
    this.subscription?.unsubscribe()

    const consumer = getConsumer(this.spaceGroupIdValue || "default")
    this.subscription = consumer.subscriptions.create(
      {
        channel: "AIChatChannel",
        ai_chat_id: chatId,
        space_scope: currentSpaceScope(),
        // Only meaningful for a draft: the channel reads it when it lazily
        // creates the chat, and re-verifies the user can see that agent.
        // It rides along inert on a non-null chatId — AIChatChannel#subscribed
        // reads params[:ai_agent_id] ONLY inside #lazily_create_chat, so the
        // real-id branch (including the draft-promotion swap below) never
        // consults it and cannot rebind an existing chat's persona. Anything
        // that teaches that branch to read it must clear pendingAgentId on
        // promotion first.
        ai_agent_id: this.pendingAgentId || undefined,
      },
      {
        received: (data) => this.handleMessage(data),
        connected: () => {
          this.isConnected = true
          this.rejectedChatId = null
          this.setStatus(this.connectedStatusValue)
        },
        disconnected: (payload) => {
          this.isConnected = false
          this.setStatus(this.reconnectingStatusValue)
          this.#resubscribeIfDraftPromoted(chatId, payload)
        },
        rejected: () => this.#handleSubscriptionRejected(chatId),
      },
    )
  }

  // CONEK-6915 — a chat started in this page session subscribes as a DRAFT
  // (`ai_chat_id: null`). The first message lazily persists the AIChat and the
  // server transmits the real id, which onConnected stores in chatIdValue — but
  // the ActionCable subscription object keeps the identifier it was built with,
  // and `Subscriptions#reload()` re-sends that identifier verbatim on every
  // reconnect. So the socket came back on `ai_chat_id: null`,
  // AIChatChannel#subscribed took its draft branch, answered `history
  // {messages: []}`, and onHistory replaced the transcript with nothing: the
  // user's own line, the answer and every card gone, back to "How can I help?".
  // The rows were never lost (a reload brings the thread back), but the
  // server-side channel also held no AIChat any more, so the next message would
  // have minted a SECOND thread and split the conversation.
  //
  // The identifier is swapped HERE, on disconnect, and deliberately not the
  // moment the draft is promoted: `process_with_agent` runs inline inside the
  // channel's `message` action, so tearing the subscription down mid-turn would
  // drop its direct `transmit_with_event` frames and replay history over the
  // half-streamed answer. Once the socket is gone there is nothing left to lose,
  // and the reconnect then replays the thread exactly as a page load does.
  //
  // #subscribe unsubscribes the old subscription first, and ActionCable's
  // `forget` REASSIGNS `subscriptions` rather than splicing it, so swapping from
  // inside notifyAll's map is safe.
  #resubscribeIfDraftPromoted(subscribedChatId, payload) {
    // An explicit consumer.disconnect() (or an unsupported protocol) stops the
    // ConnectionMonitor; re-subscribing would call ensureActiveConnection() and
    // reopen the socket the caller just closed for good.
    if (payload?.willAttemptReconnect === false) return
    // Already subscribed with a real id — nothing stale to fix. This is also
    // what bounds the swap to ONE per promoted draft: the replacement carries
    // the real id, so a reconnect storm can never turn into a subscribe storm.
    if (subscribedChatId) return
    // Still a draft: nothing to re-point at, and `null` is the correct id.
    if (!this.chatIdValue) return

    this.#subscribe(this.chatIdValue)
  }

  // AIChatChannel#subscribed calls `reject` when the requested ai_chat_id
  // does not resolve to one of this user's kept chats. ActionCable then fires
  // `rejected` and NEVER `connected`, so without this handler isConnected
  // stayed false for the life of the page and every send hit the
  // disconnected-send guard: the permanent "Not connected to Conek AI" banner.
  //
  // The id is not necessarily the user's fault — the companion_current_chat_id
  // cookie outlives the row it points at (db:reset/reseed, a chat deleted from
  // another device, a tenant switch), and only the rail's own delete path
  // clears it. So drop the stale cookie and retry ONCE as a draft, which the
  // channel always accepts. A reject on the draft itself means the rejection
  // is about the user (no ai.use, ai.enabled off), not the chat — surface the
  // error instead of re-subscribing forever.
  #handleSubscriptionRejected(chatId) {
    this.isConnected = false
    this.#clearCurrentChatCookie()

    if (chatId && this.rejectedChatId !== chatId) {
      this.rejectedChatId = chatId
      console.warn(
        `[ai--companion] AIChatChannel rejected chat ${chatId}; falling back to a new thread`,
      )
      // Not #switchChat(null): that early-returns on a same id only for
      // non-null, but it also clears the transcript and re-focuses — which is
      // exactly what we want here, since the rejected chat rendered nothing.
      this.#switchChat(null)
      return
    }

    this.setStatus(this.reconnectingStatusValue)
    this.setError(this.notConnectedMessageValue)
  }

  #clearCurrentChatCookie() {
    document.cookie =
      "companion_current_chat_id=; path=/; SameSite=Lax; max-age=0"
  }

  // Full-page shell: keep the address bar on the selected chat so a thread
  // is linkable and a reload lands on it. Every chat in the rail used to leave
  // the URL on whichever id the page was opened with. replaceState — never
  // pushState — so no history entry piles up per click and Turbo's popstate
  // handling never gets a reason to visit; the existing history.state is
  // passed through so Turbo's restoration identifier survives. A draft (no
  // id yet) points at the index; onConnected re-syncs once the server mints
  // the id. No-op on the panel, which has no chatsPath.
  #syncChatUrl(chatId) {
    if (!this.hasChatsPathValue || !this.chatsPathValue) return
    if (typeof history?.replaceState !== "function") return
    const base = this.chatsPathValue.replace(/\/+$/, "")
    const path = chatId ? `${base}/${encodeURIComponent(chatId)}` : base
    if (window.location.pathname === path) return
    try {
      history.replaceState(
        history.state,
        "",
        `${path}${window.location.search}`,
      )
    } catch {
      // Cross-origin or sandboxed document: the transcript still switched,
      // the address bar simply stays behind.
    }
  }

  // Panel-only sibling of #syncChatUrl: keep the maximize control's href on
  // the thread the panel is actually showing. The link is a server-rendered
  // <a> baked from the last-active-chat cookie at page render, so it goes
  // stale the moment this controller switches chats or a draft is promoted to
  // a real one — maximising then dropped the user on whichever thread the
  // page was rendered with (CONEK-6740 QA). The href is always
  // `<space>/companion/chats` optionally followed by one id segment, so the
  // base is recoverable without the panel knowing the space prefix. No-op on
  // the full page, which renders no such anchor.
  #updateMaximizeLink(chatId) {
    if (!this.hasMaximizeLinkTarget) return
    const href = this.maximizeLinkTarget.getAttribute("href") || ""
    const base = href.match(/^(.*\/companion\/chats)(?:\/[^/]*)?$/)?.[1] ?? href
    this.maximizeLinkTarget.setAttribute(
      "href",
      chatId ? `${base}/${encodeURIComponent(chatId)}` : base,
    )
  }

  // Re-point the subscription at a different chat (or null for a fresh
  // thread). Tears down the old subscription, clears the rendered
  // messages, then re-subscribes — the server's replay_history pushes
  // the selected chat's turns back down as a `history` event.
  #switchChat(chatId) {
    if (chatId && chatId === this.chatIdValue) return
    // Selecting an existing chat abandons any pending agent pick: that chat's
    // binding was fixed when it was created and cannot be rebound.
    if (chatId) this.pendingAgentId = null
    this.subscription?.unsubscribe()
    this.subscription = null
    // Everything that belonged to the turn in flight on the OLD thread goes
    // with it: the half-revealed bubble's pacing engine, the seq reorder
    // window, and the "Thinking… / Calling <tool>…" indicator. Without the
    // last one, "New chat" during a tool call left "Calling list_drawings…"
    // sitting under an empty transcript — the old subscription is gone, so no
    // `done` frame was ever going to clear it.
    this.#dropStream()
    this.#resetSeqState()
    this.streamingMessageId = null
    this.setLoading(false)
    // The turn of the OLD thread is over for this client: its `done` never
    // arrives once the subscription is gone. Left set, `_turnRunning` hides
    // Regenerate on the new chat and a stale `_awaitingTurn` makes Stop send
    // `seq: 0`. A turn the new chat really has in flight is picked up by
    // onTurnInFlight, whose onThinking mints `streamingMessageId` — that, not
    // `_turnRunning`, keeps Regenerate hidden for a resumed turn.
    this._turnRunning = false
    this._awaitingTurn = false
    this._stoppedTurn = null
    this._steps = null
    // Queued attachments bind to a chat at selection time (the upload pins
    // ai_chat_id), so carrying them across a switch would either send files
    // resolved for the abandoned chat or have the server silently filter
    // them out — clear them like the rest of the per-thread composer state.
    this.pendingAttachments = []
    this.threadEpoch += 1
    this.#renderComposerStatus()
    this.#clearAllTimers()
    this.#clearSkeletonTimeout()
    if (this.hasMessagesTarget) this.messagesTarget.innerHTML = ""
    this.#finalizeStall()
    this.#syncTranscriptState()
    this.chatIdValue = chatId ?? ""
    this.#syncChatUrl(chatId)
    this.#updateMaximizeLink(chatId)
    this.#subscribe(chatId)
    // Composer focus on chat switch (workstream 3 sub-phase 3) — mirrors
    // the React reference's effect on `currentChatId` (CompanionSidebar.tsx).
    this.inputTarget?.focus()
  }

  #clearAllTimers() {
    this.ackTimeouts.forEach((timer) => clearTimeout(timer))
    this.ackTimeouts.clear()
    this.stallTimeouts.forEach((timer) => clearTimeout(timer))
    this.stallTimeouts.clear()
    this.lastSentUserMessageId = null
  }

  disconnect() {
    if (this._handleMessagesScroll && this.hasMessagesTarget) {
      this.messagesTarget.removeEventListener(
        "scroll",
        this._handleMessagesScroll,
      )
      this.messagesTarget.removeEventListener(
        "wheel",
        this._handleMessagesWheel,
      )
      this._handleMessagesScroll = null
      this._handleMessagesWheel = null
    }
    clearTimeout(this._recordChangedTimer)
    this._recordChangedTimer = null
    this.subscription?.unsubscribe()
    this.subscription = null
    this.#dropStream()
    this.#resetSeqState()
    this.#clearAllTimers()
    this.#clearSkeletonTimeout()
    if (this._handleCompanionContext) {
      window.removeEventListener(
        "companion:context",
        this._handleCompanionContext,
      )
      this._handleCompanionContext = null
    }
    if (this._handleCompanionContextCleared) {
      window.removeEventListener(
        "companion:context-cleared",
        this._handleCompanionContextCleared,
      )
      this._handleCompanionContextCleared = null
    }
    if (this._handleRailSelectChat) {
      this.element.removeEventListener(
        "ai--chat-list-rail:select-chat",
        this._handleRailSelectChat,
      )
      this._handleRailSelectChat = null
    }
    if (this._handleRailNewChat) {
      this.element.removeEventListener(
        "ai--chat-list-rail:new-chat",
        this._handleRailNewChat,
      )
      this._handleRailNewChat = null
    }
    if (this._handlePickAgent) {
      window.removeEventListener("companion:pick-agent", this._handlePickAgent)
      this._handlePickAgent = null
    }
    // Release the microphone. Stopping the recorder fires its `stop` handler,
    // which stops the stream tracks — without this a Turbo navigation away
    // from the page would leave the browser's recording indicator on.
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop()
    }
    this.mediaRecorder = null
    this.pendingAttachments = []
  }

  // CONEK-3366 — Render the context chip strip and swap the composer
  // placeholder when the user navigates to a page that broadcasts an entity
  // (currently support tickets; opens to any entity via companion_context_controller).
  handleCompanionContext(event) {
    const detail = event?.detail
    if (!detail) return

    // An entity is now OPTIONAL. A list, a calendar month or a dashboard
    // range has no single "current record", and that is the common case, not
    // the exception — CompanionPageState.tsx and calendars/show.html.erb both
    // broadcast a `page_state` envelope with no entity at all. The server has
    // had a branch for exactly that since GA programme F2.1
    // (CompanionAgent#build_context_info falls through to page_state_info),
    // but this handler used to require BOTH entityType and entityId, so the
    // envelope was dropped here and that branch was unreachable in practice.
    const hasEntity = Boolean(detail.entityType && detail.entityId)

    // Remember the current context so sendMessage() can forward it as
    // `context` on the AIChatChannel#message payload. Without this the
    // chip is visual-only and the agent has no idea which entity the
    // user is asking about. CONEK-3366 chip wiring landed without the
    // context being attached to outgoing messages.
    //
    // GA programme F2.1 — a producer may also supply the RICH page-context
    // envelope (`type`, `financial`, `related`, `canvas_state`, `cart`, and the
    // drawing-editor branch's flat drawing_id/order_id/quote_name). It is
    // spread FIRST so the three flat entity fields always win: the entity a
    // producer declares must never be contradicted by its own envelope.
    // AI::CompanionContext.normalize preserves every other key untouched, and
    // CompanionAgent#build_context_info reads them — this is what closes the
    // React-chrome parity gap (React sent the envelope via use-ai-chat.ts,
    // Hotwire sent only the three flat fields).
    //
    // The entity keys are added only when there IS an entity: emitting
    // `entity_type: undefined` would send the server down its entity branch
    // with nothing to put in it.
    this.currentEntityContext = {
      ...(detail.context && typeof detail.context === "object"
        ? detail.context
        : {}),
      ...(hasEntity
        ? {
            entity_type: detail.entityType,
            entity_id: detail.entityId,
            entity_name: detail.entityName || null,
          }
        : {}),
    }

    const displayName = hasEntity
      ? detail.entityName || `${detail.entityType} ${detail.entityId}`
      : null

    // Render / replace the chip for this entity type. Entity-only: there is
    // nothing to pin a chip to on a list page, and ContextChipComponent's
    // click target is the entity's own URL.
    if (
      hasEntity &&
      this.hasContextChipsTarget &&
      this.hasContextChipsListTarget
    ) {
      // A PINNED chip is never replaced by the next page's chip of the same
      // type — that is the whole point of pinning it.
      const selector = `[data-entity-type="${cssEscape(detail.entityType)}"]:not([data-pinned])`
      const existing = this.contextChipsListTarget.querySelector(selector)
      if (existing) existing.remove()
      const alreadyPinned = this.contextChipsListTarget.querySelector(
        `[data-entity-type="${cssEscape(detail.entityType)}"][data-entity-id="${cssEscape(detail.entityId)}"]`,
      )
      if (alreadyPinned) alreadyPinned.remove()

      const chip = this.#buildContextChip(
        {
          entity_type: detail.entityType,
          entity_id: detail.entityId,
          entity_name: displayName,
          ...(detail.contactType ? { contact_type: detail.contactType } : {}),
        },
        { animate: true },
      )
      this.contextChipsListTarget.appendChild(chip)
      this.contextChipsTarget.classList.remove("hidden")
      // CONEK-3367 — Respect prefers-reduced-motion. AC5 maps onto this
      // existing chip-fade handshake; no separate controller.
      if (prefersReducedMotion()) {
        chip.classList.remove("opacity-0")
      } else {
        requestAnimationFrame(() => chip.classList.remove("opacity-0"))
      }
    }

    // Swap the composer placeholder to reference the current entity. Only
    // when there is one — and only when the server-rendered translated
    // template is present, so a mount missing the value keeps the default
    // placeholder instead of interpolating into "undefined" (CONEK-7341).
    if (hasEntity && this.hasInputTarget && this.askAboutTemplateValue) {
      this.inputTarget.setAttribute(
        "placeholder",
        this.askAboutTemplateValue.replace("%{entity_name}", displayName),
      )
    }

    this.#renderSuggestions(detail.suggestedActions)
  }

  // ── Pinned contexts (ai.companion_panel_v2) ─────────────────────
  // Demo 2026-09-23: "pinear contextos de diferentes páginas". The user pins
  // a record's chip; it stays in the strip as they navigate (per tab, in
  // sessionStorage) and rides every message as context.pinned, which
  // CompanionAgent#pinned_context_info bounds and lists for the model.
  static PINNED_LIMIT = 5
  static PINNED_STORAGE_KEY = "companion:pinned-contexts"

  pinningEnabledValueChanged(enabled) {
    if (!enabled || !this.hasContextChipsListTarget) return
    // Restore the tab's pins into the strip (reload, Turbo visit).
    for (const pin of this.#pins()) {
      const selector = `[data-entity-type="${cssEscape(pin.entity_type)}"][data-entity-id="${cssEscape(pin.entity_id)}"]`
      if (this.contextChipsListTarget.querySelector(selector)) continue
      this.contextChipsListTarget.appendChild(this.#buildContextChip(pin))
    }
    if (
      this.hasContextChipsTarget &&
      this.contextChipsListTarget.children.length
    ) {
      this.contextChipsTarget.classList.remove("hidden")
    }
  }

  #pins() {
    if (!this._pins) {
      try {
        const raw = JSON.parse(
          sessionStorage.getItem(this.constructor.PINNED_STORAGE_KEY) || "[]",
        )
        this._pins = Array.isArray(raw)
          ? raw.filter((p) => p && p.entity_type && p.entity_id)
          : []
      } catch {
        this._pins = []
      }
    }
    return this._pins
  }

  #savePins() {
    try {
      sessionStorage.setItem(
        this.constructor.PINNED_STORAGE_KEY,
        JSON.stringify(this._pins ?? []),
      )
    } catch {
      // Storage blocked (private mode): pins still work for this page.
    }
  }

  #isPinned(entity) {
    return this.#pins().some(
      (p) =>
        p.entity_type === entity.entity_type &&
        p.entity_id === entity.entity_id,
    )
  }

  // One context chip. Same look as before; the pin control is added only
  // when pinning is on. A direct listener rather than a data-action, so the
  // control works the moment the chip is appended.
  //
  // `animate`: only the chip of the page being opened fades in (the caller
  // removes opacity-0); a restored or re-pinned chip must appear as it is.
  #buildContextChip(entity, { animate = false } = {}) {
    const chip = document.createElement("span")
    chip.dataset.entityType = entity.entity_type
    chip.dataset.entityId = entity.entity_id
    // max-w-full + a truncating label: a long record name stays on one line
    // with the pin control centred beside it, instead of wrapping under it.
    chip.className = `inline-flex max-w-full items-center gap-1.5 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs transition-opacity duration-200${animate ? " opacity-0" : ""}`
    const label = document.createElement("span")
    label.className = "min-w-0 truncate"
    label.title = entity.entity_name || ""
    label.textContent =
      entity.entity_name || `${entity.entity_type} ${entity.entity_id}`
    // CONEK-5907: the label opens the record when it has a page. A type
    // with none (Drawing) stays an inert label. The pin control is a
    // sibling, so pinning never navigates.
    const navigationType = this.#chipNavigationType(entity)
    if (this.#entityPath(navigationType, entity.entity_id)) {
      label.dataset.companionChipLink = "true"
      label.setAttribute("role", "button")
      label.setAttribute("tabindex", "0")
      label.classList.add("cursor-pointer", "hover:underline")
      const open = () => this.#navigateToPath(navigationType, entity.entity_id)
      label.addEventListener("click", open)
      label.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        open()
      })
    }
    chip.appendChild(label)

    if (this.pinningEnabledValue) {
      const pinned = this.#isPinned(entity)
      if (pinned) chip.dataset.pinned = "true"
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.companionPin = "true"
      button.className =
        "inline-flex size-4 shrink-0 items-center justify-center rounded-full opacity-70 hover:opacity-100"
      const icon = pinned
        ? this.hasPinnedIconTemplateTarget && this.pinnedIconTemplateTarget
        : this.hasPinIconTemplateTarget && this.pinIconTemplateTarget
      if (icon) button.appendChild(icon.content.cloneNode(true))
      else button.textContent = pinned ? "★" : "☆"
      button.setAttribute("aria-pressed", String(pinned))
      button.setAttribute(
        "aria-label",
        pinned ? this.unpinLabelValue : this.pinLabelValue,
      )
      button.addEventListener("click", () => this.#togglePin(entity, chip))
      chip.appendChild(button)
    }
    return chip
  }

  // A Site shares the canonical "Contact" type with people but opens
  // /companies/:id, so the chip routes it as "company" (CONEK-5907).
  #chipNavigationType(entity) {
    return entity.entity_type === "Contact" && entity.contact_type === "company"
      ? "company"
      : entity.entity_type
  }

  #togglePin(entity, chip) {
    const pins = this.#pins()
    const pin = {
      entity_type: entity.entity_type,
      entity_id: entity.entity_id,
      entity_name: entity.entity_name ?? null,
      // Kept so a restored pinned Site still opens the Site page.
      ...(entity.contact_type ? { contact_type: entity.contact_type } : {}),
    }
    const current = this.currentEntityContext
    const isCurrent =
      current?.entity_type === pin.entity_type &&
      current?.entity_id === pin.entity_id

    if (this.#isPinned(pin)) {
      this._pins = pins.filter(
        (p) =>
          !(p.entity_type === pin.entity_type && p.entity_id === pin.entity_id),
      )
      // Still the page's own record: keep its chip, just unpinned.
      if (isCurrent) chip.replaceWith(this.#buildContextChip(pin))
      else chip.remove()
    } else {
      this._pins = [...pins, pin]
      // Past the limit the OLDEST pin goes, with its chip.
      while (this._pins.length > this.constructor.PINNED_LIMIT) {
        const dropped = this._pins.shift()
        this.contextChipsListTarget
          ?.querySelector(
            `[data-pinned][data-entity-type="${cssEscape(dropped.entity_type)}"][data-entity-id="${cssEscape(dropped.entity_id)}"]`,
          )
          ?.remove()
      }
      chip.replaceWith(this.#buildContextChip(pin))
    }
    this.#savePins()
    if (this.hasContextChipsTarget && this.hasContextChipsListTarget) {
      this.contextChipsTarget.classList.toggle(
        "hidden",
        this.contextChipsListTarget.children.length === 0,
      )
    }
  }

  // GA programme F2 — page-specific suggestion chips, the last chrome parity
  // gap against the deleted React CompanionSidebar (which derived its chips
  // from the registered page context; this surface had only a static i18n
  // list). `actions` is [{label, prompt}]; anything else restores the
  // server-rendered generic set.
  //
  // Built with createElement + textContent, never innerHTML: label and prompt
  // originate from page data and must never be parsed as markup.
  #renderSuggestions(actions) {
    if (!this.hasSuggestionsTarget) return

    const valid = Array.isArray(actions)
      ? actions.filter((a) => a && a.label && a.prompt)
      : []

    // The chip is SuggestionChipComponent's own render, cloned — the same
    // markup as the server-rendered defaults, so the two cannot drift.
    if (valid.length === 0 || !this.hasSuggestionChipTemplateTarget) {
      this.#restoreDefaultSuggestions()
      return
    }

    this.suggestionsTarget.replaceChildren(
      ...valid.map((action) => {
        const chip = this.#cloneTemplate(this.suggestionChipTemplateTarget)
        chip.dataset.prompt = action.prompt
        chip.textContent = action.label
        return chip
      }),
    )
  }

  #restoreDefaultSuggestions() {
    if (!this.hasSuggestionsTarget) return
    if (this.defaultSuggestionsHtml === null) return
    if (this.suggestionsTarget.innerHTML === this.defaultSuggestionsHtml) return
    // Restoring the exact server-rendered markup captured in connect() — this
    // is our own trusted, i18n'd HTML, not page or model data.
    this.suggestionsTarget.innerHTML = this.defaultSuggestionsHtml
  }

  // CONEK-3366 — Symmetric reset when the broadcaster disconnects (user
  // navigates from /support/tickets/:id back to /support/tickets). Restores
  // the placeholder and clears the chip strip so stale per-entity context
  // doesn't bleed into the next page.
  handleCompanionContextCleared() {
    this.currentEntityContext = null
    if (this.hasContextChipsListTarget) {
      // Pinned chips (ai.companion_panel_v2) outlive the page that
      // showed them; everything else goes.
      this.contextChipsListTarget
        .querySelectorAll(":scope > :not([data-pinned])")
        .forEach((chip) => chip.remove())
    }
    if (this.hasContextChipsTarget) {
      const empty =
        !this.hasContextChipsListTarget ||
        this.contextChipsListTarget.children.length === 0
      this.contextChipsTarget.classList.toggle("hidden", empty)
    }
    if (this.hasInputTarget && this.defaultPlaceholder !== null) {
      this.inputTarget.setAttribute("placeholder", this.defaultPlaceholder)
    }
    this.#restoreDefaultSuggestions()
  }

  // ──────────────────────────────────────────────────────────────────────
  // Generative-UI card actions (CONEK-189 Sentry). The server-rendered card
  // ViewComponents (AI::Companion::SearchResultsComponent & friends) bind
  // these — they were the click callbacks of the deleted React generative-UI
  // components and were never ported, so every card click threw
  // "references undefined method".
  //
  // Path mapping mirrors CompanionAgent::NavigationTools#navigate_to: raw
  // relative paths are correct — the routes catch-all
  // (space_redirects#redirect_to_space) rewrites an un-prefixed path onto the
  // user's space prefix, and onNavigation applies the scheme allowlist.

  #entityPath(entityType, entityId) {
    // Producers disagree on casing: SearchResultsComponent emits lowercase
    // ("contact"), ContextChipComponent emits the canonical
    // AI::CompanionEntityTypes values ("Contact", "SupportTicket"). Snake-case
    // the key so both resolve. "Drawing" stays unmapped on purpose — drawings
    // have no standalone page (they live inside a quote).
    const key = String(entityType ?? "")
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .toLowerCase()
    const collection = {
      contact: "/contacts",
      company: "/companies",
      opportunity: "/opportunities",
      task: "/tasks",
      // Added with the My Day briefing, which surfaces today's meetings as
      // rows. Without this entry every meeting row was inert — #entityPath
      // returns null for an unmapped type and #navigateToPath then silently
      // does nothing, which looks identical to a broken click.
      booking: "/bookings",
      support_ticket: "/support/tickets",
      // "quote" => Order legacy alias, as in CompanionEntityTypes.
      order: "/quotes",
      quote: "/quotes",
      item: "/catalog/items",
      // The remaining SearchResultsComponent RESULT_TYPES: a "product" search
      // result is a catalog item; a "document" id is a doc-page slug
      // (/docs/*slug, outside the space scope so it resolves un-prefixed).
      product: "/catalog/items",
      document: "/docs",
    }[key]
    if (!collection) return null
    if (!entityId) return collection

    // Encode per path segment, not the whole id: a doc-page slug mirrors the
    // docs directory tree and can itself contain slashes ("user/mcp"), which
    // a whole-id encodeURIComponent would turn into %2F — the /docs splat
    // then sees one segment with a literal %2F and DocPage.find 404s.
    const encodedId = String(entityId)
      .split("/")
      .map(encodeURIComponent)
      .join("/")

    return `${collection}/${encodedId}`
  }

  #navigateToPath(entityType, entityId) {
    const url = this.#entityPath(entityType, entityId)
    // entity_type rides along so the Inertia bridge can tell an Inertia-served
    // page (task, quote…) from a Hotwire one (support ticket, booking) — the
    // same key the agent's `navigation` frames already carry.
    if (url) this.onNavigation({ url, entity_type: entityType })
  }

  handleSearchResultClick(event) {
    this.#navigateToPath(event.params.resultType, event.params.resultId)
  }

  handleContactClick(event) {
    this.#navigateToPath("contact", event.params.contactId)
  }

  handleOpportunityNavigate(event) {
    this.#navigateToPath("opportunity", event.params.opportunityId)
  }

  handleContactAction(event) {
    // Parity with the React ContactCard: only the "view" action navigated —
    // other action types existed in the props contract but no producer ever
    // wired them, so they stay inert rather than guessing a behavior.
    if (event.params.actionType !== "view") return
    // CONEK-9296: a Site card carries a ContactCompany id, which /contacts/:id
    // (people only) answers with "Contact not found". A card without the
    // param (older gem, replayed card) keeps opening the person page.
    const entityType =
      event.params.contactType === "company" ? "company" : "contact"
    this.#navigateToPath(entityType, event.params.contactId)
  }

  // Selecting an option sends it as a message through the ordinary send path
  // (same as sendStarterPrompt), naming the ID explicitly so the agent uses it
  // directly instead of re-searching by label — the React chrome's contract.
  handleOptionSelect(event) {
    if (!this.hasInputTarget) return
    const { optionId, optionName } = event.params
    if (!optionId) return
    this.inputTarget.value = `Use "${optionName || optionId}" (ID: ${optionId})`
    this.sendMessage()
  }

  // The edit CTA is only rendered when a producer passes show_edit: true
  // (today: the Lookbook preview alone; the React chrome never wired onEdit
  // either). Focus the composer so the user can type an amended instruction.
  handleActionEdit() {
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  navigateToEntity(event) {
    this.#navigateToPath(event.params.entityType, event.params.entityId)
  }

  // Dismiss one context chip. Mirrors the React chrome's per-chip dismissal:
  // the chip disappears and, when it was the entity backing
  // currentEntityContext, outgoing messages stop carrying that context.
  removeContextChip(event) {
    event.stopPropagation()
    const { entityType, entityId } = event.params
    event.currentTarget.closest("[data-entity-type]")?.remove()

    if (
      this.currentEntityContext &&
      this.currentEntityContext.entity_type === entityType &&
      this.currentEntityContext.entity_id === entityId
    ) {
      this.currentEntityContext = null
      if (this.hasInputTarget && this.defaultPlaceholder !== null) {
        this.inputTarget.setAttribute("placeholder", this.defaultPlaceholder)
      }
    }

    if (
      this.hasContextChipsTarget &&
      this.hasContextChipsListTarget &&
      this.contextChipsListTarget.children.length === 0
    ) {
      this.contextChipsTarget.classList.add("hidden")
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Composer actions

  sendMessage() {
    if (!this.hasInputTarget) return
    const text = this.inputTarget.value.trim()
    if (text.length === 0) return

    if (this.#sendUserMessage(text)) this.inputTarget.value = ""
  }

  // Core send path, shared by sendMessage() and handleRetryMessage().
  // Returns true if the message was actually dispatched (false when the
  // disconnected-send guard fires), so callers can decide whether to clear
  // the composer / drop the failed bubble.
  #sendUserMessage(text) {
    // Disconnected-send guard (workstream 3 sub-phase 2): sending while the
    // cable is down would show a bubble that can never be acked. Mirrors
    // the React reference (use-ai-chat.ts#sendMessage), which returns early
    // before adding the message when !isConnected.
    if (!this.isConnected) {
      this.setError(this.notConnectedMessageValue)
      return false
    }

    // Forward the current entity context (set by handleCompanionContext)
    // so the agent knows which ticket / quote / contact the user is on.
    // Empty when the user is on a list/index page with no per-entity broadcast.
    const context = this.currentEntityContext
      ? { ...this.currentEntityContext }
      : {}
    // ai.companion_panel_v2 — every pinned record except the one the
    // user is looking at (that one is already the entity above).
    if (this.pinningEnabledValue) {
      const pinned = this.#pins().filter(
        (pin) =>
          !(
            pin.entity_type === context.entity_type &&
            pin.entity_id === context.entity_id
          ),
      )
      if (pinned.length) context.pinned = pinned
    }
    const payload = {
      message: text,
      context,
      history: this.#historyForServer(),
    }
    // Attachments ride the same frame. No model rides it: the model is a
    // platform decision, so there is nothing here for the client to choose.
    if (this.pendingAttachments?.length) {
      payload.attachment_ids = this.pendingAttachments.map((a) => a.id)
    }
    const messageId = crypto.randomUUID()
    // The server does NOT broadcast an echo of the user's message back over
    // the cable (only assistant/tool/done events). If we don't render it
    // locally, the user sees their input disappear from the composer and
    // never appear in the messages list. Mirror the React side
    // (use-ai-chat.ts) and append the bubble immediately.
    this.appendTextBubble("user", text, messageId)
    this.#setBubbleState(messageId, "sending")
    this.lastSentUserMessageId = messageId
    // A new turn starts: until its `thinking` names it, `_seqTurn` is stale.
    // A stop still waiting to learn its turn id gives up here — otherwise it
    // would claim THIS turn's frames and swallow the answer.
    this._awaitingTurn = true
    if (this._stoppedTurn === STOP_PENDING) this._stoppedTurn = null
    this._stalledBubble = null
    this._stalledTurn = null
    this.#finishSteps()
    this._turnRunning = true
    this.#syncRegenerate()
    this.#perform("message", payload)
    this.#scheduleAckTimeout(messageId, text)
    // Attachments belong to the turn that sent them, not to the composer.
    this.pendingAttachments = []
    this.#renderComposerStatus()
    return true
  }

  // Multi-turn memory: send the last HISTORY_TURNS role/content pairs with
  // every message, so the model sees the conversation, not just the current
  // line. The React reference (use-ai-chat.ts) used to send slice(-10) of its
  // messages array; the Hotwire rewrite regressed this to `history: []`,
  // which meant every turn reached the model as if it were the first (flagged
  // by the 2026-09 deep review of the cutover commit).
  //
  // The DOM IS the transcript here: every user echo and finalized assistant
  // answer is a `[data-role]` bubble under messagesTarget, and history
  // replay / retry-removal keep it faithful. Assistant text is read back as
  // the markdown stripped to prose — which is exactly the conversational
  // signal the server replays via build_conversation.
  // Excludes the message being sent right now: it is captured into the DOM
  // only AFTER the payload is built, and the server appends the final turn
  // itself. Errors render in the error banner (no data-role), so failed
  // transports never pollute the history — and in-bubble Retry buttons are
  // stripped (#bubbleTranscriptText), so a failed turn's "Retry" chrome never
  // leaks into the replayed conversation either.
  #historyForServer() {
    const HISTORY_TURNS = 10
    if (!this.hasMessagesTarget) return []

    const pairs = []
    for (const bubble of this.messagesTarget.querySelectorAll("[data-role]")) {
      const role = bubble.dataset.role
      if (role !== "user" && role !== "assistant") continue
      const content = this.#bubbleTranscriptText(bubble)
      if (content) pairs.push({ role, content })
    }
    return pairs.slice(-HISTORY_TURNS)
  }

  // The transcript text of a bubble EXCLUDING interactive chrome. Retry
  // buttons are appended INSIDE the [data-role] bubble
  // (#markUserMessageFailed / #markAssistantMessageForRetry), so a raw
  // textContent read would leak the literal "Retry" into the model's history
  // — and, via #findTriggeringUserText, into the retry param, where a retried
  // turn would re-send "…Retry" as the user's question. Cloning and stripping
  // keeps the read faithful to what the user actually wrote and received.
  #bubbleTranscriptText(bubble) {
    if (!bubble) return ""
    const clone = bubble.cloneNode(true)
    clone
      .querySelectorAll("[data-companion-retry], [data-companion-marker-label]")
      .forEach((el) => el.remove())
    return (clone.textContent ?? "").trim()
  }

  // ──────────────────────────────────────────────────────────────────────
  // Composer controls (model picker / attachments / voice).
  //
  // Each is optional: the server omits the control entirely when its flag is
  // off, so every method here no-ops when its target is absent. State is kept
  // on the instance rather than in Stimulus values because none of it should
  // survive a reconnect — a half-attached image or an abandoned recording must
  // not reappear.

  openAttachmentPicker() {
    this.attachmentInputTarget?.click()
  }

  async attachmentSelected(event) {
    const file = event.target?.files?.[0]
    // Reset immediately so selecting the same file twice still fires `change`.
    if (event.target) event.target.value = ""
    if (!file) return

    this.pendingAttachments ??= []
    this.#renderComposerStatus(this.attachmentUploadingLabelValue)
    // The request cannot be cancelled once in flight, so compare epochs when it
    // settles: a switch in between means the file was uploaded for the thread
    // the user already left (and pinned to it via ai_chat_id).
    const epoch = this.threadEpoch

    try {
      const body = new FormData()
      body.append("file", file)
      // Scopes the upload to the current thread. Absent on a draft, where the
      // chat row does not exist yet — the server treats it as optional.
      if (this.chatIdValue) body.append("ai_chat_id", this.chatIdValue)
      const response = await post(this.attachmentUrlValue, { body })
      if (!response.ok) throw new Error(`upload failed: ${response.statusCode}`)

      const json = await response.json
      if (!json?.id) throw new Error("upload returned no id")

      // Queuing it now would show a chip on a chat whose server-side filter
      // (ai_chat_id) drops it, so the answer would silently ignore the file.
      if (epoch !== this.threadEpoch) return

      this.pendingAttachments.push({
        id: json.id,
        filename: json.filename || file.name,
      })
      this.#renderComposerStatus()
    } catch (error) {
      console.warn("[ai--companion] attachment upload failed", error)
      // Failure of an abandoned thread's upload is not the new thread's news,
      // and re-rendering would wipe a newer upload's "Uploading…" line.
      if (epoch !== this.threadEpoch) return
      this.setError(this.attachmentFailedMessageValue)
      this.#renderComposerStatus()
    }
  }

  async toggleVoiceRecording() {
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop()
      return
    }

    // Feature-detect rather than assume: MediaRecorder is absent in some
    // embedded webviews, and getUserMedia is unavailable on insecure origins.
    if (
      typeof MediaRecorder === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      this.setError(this.voiceUnsupportedMessageValue)
      return
    }

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      // A denied permission is a normal outcome, not a bug — say so plainly
      // rather than surfacing a generic failure.
      console.warn("[ai--companion] microphone unavailable", error)
      this.setError(this.voiceDeniedMessageValue)
      return
    }

    const chunks = []
    const recorder = new MediaRecorder(stream)
    this.mediaRecorder = recorder
    this.#setVoiceRecordingState(true)

    recorder.addEventListener("dataavailable", (e) => {
      if (e.data?.size) chunks.push(e.data)
    })
    recorder.addEventListener("stop", async () => {
      // Always release the mic, whatever happens to the audio.
      stream.getTracks().forEach((track) => track.stop())
      this.mediaRecorder = null
      this.#setVoiceRecordingState(false)
      if (!chunks.length) return

      await this.#transcribe(
        new Blob(chunks, { type: recorder.mimeType || "audio/webm" }),
      )
    })

    recorder.start()
  }

  // Uploads the recording and puts the transcript in the composer for the user
  // to read and edit. It deliberately does NOT auto-send: a mis-transcribed
  // instruction that reaches the agent unreviewed can trigger a tool call the
  // user never asked for.
  async #transcribe(blob) {
    this.#renderComposerStatus(this.voiceTranscribingLabelValue)

    try {
      const body = new FormData()
      body.append("audio", blob, "recording.webm")
      const response = await post(this.transcriptionUrlValue, { body })
      if (!response.ok)
        throw new Error(`transcription failed: ${response.statusCode}`)

      const json = await response.json
      const text = json?.text?.trim()
      if (!text) throw new Error("transcription returned no text")

      if (this.hasInputTarget) {
        const existing = this.inputTarget.value
        this.inputTarget.value = existing ? `${existing} ${text}` : text
        this.inputTarget.focus()
      }
    } catch (error) {
      console.warn("[ai--companion] transcription failed", error)
      this.setError(this.voiceFailedMessageValue)
    } finally {
      this.#renderComposerStatus()
    }
  }

  #setVoiceRecordingState(recording) {
    if (!this.hasVoiceButtonTarget) return

    this.voiceButtonTarget.dataset.recording = recording ? "true" : "false"
    this.voiceButtonTarget.setAttribute(
      "aria-label",
      recording ? this.voiceStopLabelValue : this.voiceStartLabelValue,
    )
    this.voiceButtonTarget.classList.toggle("text-danger-500", recording)
    this.#renderComposerStatus(recording ? this.voiceRecordingLabelValue : null)
  }

  // One line under the textarea for whatever the composer is currently doing:
  // recording, transcribing, uploading, or the files queued for the next turn.
  // Hidden when there is nothing to say so the box keeps its resting height.
  //
  // Queued files are listed BY FILENAME rather than as a count, which also
  // sidesteps client-side pluralisation — this controller has no i18n runtime,
  // only the pre-translated strings the server injected as values.
  #renderComposerStatus(message = null) {
    if (!this.hasComposerStatusTarget) return

    const target = this.composerStatusTarget
    target.replaceChildren()

    if (message) {
      target.appendChild(this.#statusLine(message))
      target.classList.remove("hidden")
      return
    }

    const queued = this.pendingAttachments ?? []
    if (!queued.length) {
      target.classList.add("hidden")
      return
    }

    const list = document.createElement("ul")
    list.className = "flex flex-wrap gap-1"
    queued.forEach(({ id, filename }) => {
      const item = document.createElement("li")
      item.className =
        "bg-muted text-muted-foreground inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-2xs"
      const name = document.createElement("span")
      name.className = "max-w-32 truncate"
      // textContent, never innerHTML: the filename comes from the user's own
      // filesystem and is echoed back by the upload endpoint.
      name.textContent = filename
      item.appendChild(name)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "hover:text-foreground"
      remove.setAttribute("aria-label", this.attachmentRemoveLabelValue)
      remove.textContent = "×"
      remove.addEventListener("click", () => this.#removeAttachment(id))
      item.appendChild(remove)

      list.appendChild(item)
    })
    target.appendChild(list)
    target.classList.remove("hidden")
  }

  #statusLine(text) {
    const line = document.createElement("p")
    line.className = "text-muted-foreground text-2xs"
    line.textContent = text
    return line
  }

  #removeAttachment(id) {
    this.pendingAttachments = (this.pendingAttachments ?? []).filter(
      (attachment) => attachment.id !== id,
    )
    this.#renderComposerStatus()
  }

  // ──────────────────────────────────────────────────────────────────────
  // Reliability layer (workstream 3 sub-phase 2) — ack timeout, stall
  // detection, retry. Mirrors use-ai-chat.ts's ackTimeoutsRef /
  // assistantStreamTimeoutsRef / retryMessage, adapted to direct DOM
  // mutation instead of React state.

  // If no `thinking` ack arrives for this user message within the timeout,
  // mark it failed and offer a retry. Cleared by #clearAckTimeout when the
  // ack (or a later retry) arrives first.
  #scheduleAckTimeout(messageId, text) {
    const timer = setTimeout(() => {
      this.ackTimeouts.delete(messageId)
      if (this.lastSentUserMessageId === messageId) {
        this.lastSentUserMessageId = null
      }
      this.setLoading(false)
      this.#markUserMessageFailed(messageId, text)
      this.setError(this.timedOutMessageValue)
    }, this.constructor.ACK_TIMEOUT_MS)
    this.ackTimeouts.set(messageId, timer)
  }

  #clearAckTimeout(messageId) {
    const timer = this.ackTimeouts.get(messageId)
    if (timer) {
      clearTimeout(timer)
      this.ackTimeouts.delete(messageId)
    }
  }

  // (Re)schedules the stall timer for the currently streaming assistant
  // bubble. Called on `thinking` (start) and on every `response` chunk
  // (reset) — if the timer fires, no chunk arrived for the full window and
  // the bubble is marked stalled with a retry CTA.
  #scheduleStallTimeout(messageId, delayMs = this.constructor.ACK_TIMEOUT_MS) {
    this.#clearStallTimeout(messageId)
    const timer = setTimeout(() => {
      this.stallTimeouts.delete(messageId)
      this.#markAssistantMessageStalled(messageId)
      this.setLoading(false)
    }, delayMs)
    this.stallTimeouts.set(messageId, timer)
  }

  #clearStallTimeout(messageId) {
    const timer = this.stallTimeouts.get(messageId)
    if (timer) {
      clearTimeout(timer)
      this.stallTimeouts.delete(messageId)
    }
  }

  #setBubbleState(messageId, state) {
    const bubble = this.messagesTarget?.querySelector(
      `[data-message-id="${cssEscape(messageId)}"]`,
    )
    bubble?.setAttribute("data-state", state)
    return bubble
  }

  // Marks a sent-but-unacked user bubble as failed and appends a Retry
  // button. The button carries the original text as a param so retrying
  // doesn't depend on re-reading (possibly since-cleared) DOM content.
  #markUserMessageFailed(messageId, text) {
    const bubble = this.#setBubbleState(messageId, "failed")
    if (!bubble || bubble.querySelector("[data-companion-retry]")) return
    bubble.classList.add("border", "border-destructive/50")
    bubble.appendChild(this.#buildRetryButton(messageId, text))
  }

  // The turn was stopped: the user pressed Stop, or the history replays a turn
  // the server persisted as stopped. Says so — the "Stopped" label.
  #markAssistantMessageInterrupted(messageId) {
    this.#markAssistantMessageForRetry(messageId, {
      state: "interrupted",
      onTrailingSentence: true,
      label: this.#buildMarkerLabel(
        "ai-companion-interrupted-label",
        this.interruptedLabelValue,
      ),
    })
  }

  // CONEK-7585 — no output for the full stall window. Nobody stopped the turn,
  // and the server may still be answering, so this says it did not respond in
  // time (the ack timeout's wording) rather than "Stopped".
  #markAssistantMessageStalled(messageId) {
    // Still waiting for the agent's first turn-stamped frame: whichever turn
    // stamps one next is the one that stalled.
    this._stalledTurn = this._awaitingTurn ? null : this._seqTurn
    this._stalledBubble = this.#markAssistantMessageForRetry(messageId, {
      state: "stalled",
      label: this.#buildMarkerLabel(
        "ai-companion-stalled-label",
        this.timedOutMessageValue,
      ),
    })
  }

  // Marks an assistant bubble whose turn did not finish (`state` and `label`
  // say why) and appends a Retry button. Retrying re-sends the ORIGINAL USER
  // MESSAGE it was responding to — costs a second LLM call, but is UX-honest:
  // there is no way to "resume" a stream that already stalled server-side.
  #markAssistantMessageForRetry(
    messageId,
    { state, label, onTrailingSentence = false },
  ) {
    // The stall timer is scheduled the moment `thinking` arrives, before
    // any `response` chunk has created the bubble — a hang with ZERO
    // chunks ever received hits this branch. Create an (empty) bubble now
    // rather than leaving the user with a spinner that silently stopped
    // and nothing else, once setLoading(false) hides it.
    //
    // CONEK-6946 — unless the turn already said something: a stop during a
    // tool call lands on the fresh id minted for the NEXT round, which has no
    // bubble, while the round's sentence ("Let me check…") sits right above.
    // The marker belongs on that sentence, not on an empty bubble below it.
    // CompanionAgent#handle_generation_cancelled makes the same choice for
    // the persisted row, so a reload draws the marker in the same place.
    // Stops only: a stall with no bubble of its own has said nothing yet, and
    // the trailing bubble may be a finished answer to an earlier question
    // (a second tab sees no user line below it) — CONEK-7585.
    const bubble =
      this.messagesTarget?.querySelector(
        `[data-message-id="${cssEscape(messageId)}"]`,
      ) ||
      (onTrailingSentence && this.#trailingAssistantTextBubble()) ||
      this.createBubble("assistant", messageId)
    if (!bubble.dataset.messageId) bubble.dataset.messageId = messageId
    const bubbleId = bubble.dataset.messageId
    bubble.setAttribute("data-state", state)
    bubble.setAttribute("aria-busy", "false")
    // Replace, not skip: Stop on a stalled turn turns its label into "Stopped".
    const current = bubble.querySelector("[data-companion-marker-label]")
    if (current) current.replaceWith(label)
    else bubble.appendChild(label)
    if (bubble.querySelector("[data-companion-retry]")) return bubble
    const triggeringText = this.#findTriggeringUserText(bubbleId)
    if (triggeringText == null) return bubble
    bubble.appendChild(this.#buildRetryButton(bubbleId, triggeringText))
    return bubble
  }

  // CONEK-7585 — what a frame means for the stalled bubble. Only the stalled
  // turn's own frames count: output proves it was only slow, and its `done` or
  // `error` ends it. Frames with no turn (the channel's, a background job's
  // card) say nothing about it — except the channel's `error` for the message
  // while the turn is still unnamed, which means it never reached the agent.
  // An error carrying a `message_id` is a card action's, not the message's.
  #settleStalledTurn(data) {
    if (!this._stalledBubble) return
    if (data.turn == null) {
      if (
        data.type === "error" &&
        data.message_id == null &&
        this._stalledTurn == null
      )
        this.#finalizeStall()
      return
    }
    // Unnamed: the next turn's ack names it. Every turn the agent runs opens
    // with `thinking` (CompanionAgent#initialize_session), so a stamped frame
    // before one is another producer's — a confirmed card action's, say.
    if (this._stalledTurn == null) {
      if (data.type !== "thinking") return
      this._stalledTurn = data.turn
    }
    if (data.turn !== this._stalledTurn) return
    if (STALL_REVIVING_FRAMES.has(data.type)) {
      this.#reviveStalledBubble()
    } else if (data.type === "done") {
      // Finished with no output after the stall: an answer already on screen
      // was complete, only its `done` was slow. An empty one keeps its Retry.
      if (this.#bubbleTranscriptText(this._stalledBubble) === "")
        this.#finalizeStall()
      else this.#reviveStalledBubble({ resumed: false })
    } else if (data.type === "error") {
      this.#finalizeStall()
    }
  }

  // The stalled turn is over: its marker and Retry stay for good.
  #finalizeStall() {
    this._stalledBubble = null
    this._stalledTurn = null
  }

  // The stalled turn produced output after all, so it was only slow: take back
  // everything the stall did, and the answer ends up an ordinary one. A bubble
  // still empty (the stall fired before any text) goes entirely; the late
  // output draws its own. `resumed: false` — the turn is already done, so the
  // indicator stays hidden.
  #reviveStalledBubble({ resumed = true } = {}) {
    const bubble = this._stalledBubble
    this.#finalizeStall()
    if (!bubble?.isConnected || bubble.dataset.state !== "stalled") return
    bubble.removeAttribute("data-state")
    bubble
      .querySelectorAll("[data-companion-retry], [data-companion-marker-label]")
      .forEach((el) => el.remove())
    if (this._stream?.bubble === bubble) {
      bubble.setAttribute("aria-busy", "true")
    } else if (this.#bubbleTranscriptText(bubble) === "") {
      bubble.closest("[data-companion-message-row]")?.remove()
      this.#syncTranscriptState()
    }
    if (!resumed) return
    // The stall hid the indicator, and Stop with it.
    this.setLoading(true, this.thinkingLabelValue)
  }

  // The assistant text bubble that is the LAST thing in the transcript, or
  // null when anything else (the user's line, a card) comes after it. Only
  // then is it the current turn's text with nothing shown below it. Never a
  // stalled one (CONEK-7585): a stall marks the bubble its own turn streams
  // into, and a Stop of that turn finds it by id, so a stalled bubble reached
  // here is another turn's — nobody stopped it, and it may still answer.
  #trailingAssistantTextBubble() {
    const lastRow = this.messagesTarget?.lastElementChild
    if (!lastRow?.matches?.("[data-companion-message-row]")) return null
    const bubble = lastRow.querySelector('[data-role="assistant"]')
    return bubble?.dataset.state === "stalled" ? null : bubble
  }

  // The note under an unfinished answer: "Stopped" says what is above it is
  // not the whole answer — the "partial" half of the interrupted state (AC 3);
  // the stalled one says the turn did not respond in time. Both carry
  // data-companion-marker-label, so both are stripped from the transcript text
  // like the Retry button (#bubbleTranscriptText), and survive a re-render of
  // the bubble's markdown (#renderBubbleMarkdown).
  #buildMarkerLabel(testId, text) {
    const label = document.createElement("p")
    label.dataset.companionMarkerLabel = "true"
    label.setAttribute("data-testid", testId)
    label.className = "not-prose mt-1 mb-0 text-xs italic text-muted-foreground"
    label.textContent = text
    return label
  }

  // An interrupted or stalled assistant bubble has no stored reference to the user
  // turn it was answering — walk the DOM backwards from it to the nearest
  // preceding user bubble, mirroring the React reference's `messages`
  // array scan (use-ai-chat.ts#retryMessage's interruptedIdx path).
  #findTriggeringUserText(assistantMessageId) {
    const assistantBubble = this.messagesTarget?.querySelector(
      `[data-message-id="${cssEscape(assistantMessageId)}"]`,
    )
    const assistantRow = assistantBubble?.closest(
      "[data-companion-message-row]",
    )
    let sibling = assistantRow?.previousElementSibling
    while (sibling) {
      const userBubble = sibling.querySelector('[data-role="user"]')
      // #bubbleTranscriptText, not textContent: if THIS assistant turn
      // interrupted an earlier retry cycle, the user bubble may already
      // carry a Retry button — leaking its label into the retry param would
      // make the retried turn re-send "…Retry" as the question.
      if (userBubble) return this.#bubbleTranscriptText(userBubble)
      sibling = sibling.previousElementSibling
    }
    return null
  }

  #buildRetryButton(messageId, text) {
    const button = document.createElement("button")
    button.type = "button"
    button.dataset.companionRetry = "true"
    // `dataset.fooBarParam =` would encode as `data-foo-bar-param` — ONE
    // dash — but Stimulus identifiers with a `--` (this controller is
    // registered as "ai--companion") require the attribute to spell that
    // out literally, so these three go through setAttribute instead of the
    // dataset camelCase shortcut.
    button.setAttribute("data-testid", "ai-companion-retry")
    button.setAttribute(
      "data-action",
      "click->ai--companion#handleRetryMessage",
    )
    button.setAttribute("data-ai--companion-message-id-param", messageId)
    button.setAttribute("data-ai--companion-retry-text-param", text)
    button.className =
      "mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-destructive underline hover:no-underline"
    button.textContent = this.retryLabelValue
    return button
  }

  // Retry CTA click handler — the SAME action serves both a failed user
  // bubble (re-send its own text) and an interrupted or stalled assistant bubble
  // (re-send the user turn it was responding to); #buildRetryButton always
  // carries the right text as a param regardless of which case it's on.
  handleRetryMessage(event) {
    const { messageId, retryText } = event.params
    if (!retryText) return

    const bubble = this.messagesTarget?.querySelector(
      `[data-message-id="${cssEscape(messageId)}"]`,
    )
    const row = bubble?.closest("[data-companion-message-row]")
    // Retrying an interrupted or stalled ASSISTANT turn re-sends the triggering user
    // turn as the message itself — so that turn's bubble must leave the
    // transcript too. Leaving it in would make #historyForServer() replay it
    // as history while the send delivers it again as the current turn, and
    // the server's build_conversation appends the current turn once more:
    // the model would read [user: Q, user: Q] as consecutive turns. (A
    // failed USER bubble removes its own row below, so this walk only
    // applies to assistant rows.)
    if (bubble?.dataset.role === "assistant") {
      let sibling = row?.previousElementSibling
      while (sibling) {
        if (sibling.querySelector('[data-role="user"]')) {
          sibling.remove()
          break
        }
        sibling = sibling.previousElementSibling
      }
    }
    row?.remove()

    this.#sendUserMessage(retryText)
  }

  handleKeydown(event) {
    // Enter sends; Shift+Enter inserts a newline (matches React +
    // every other chat UI convention). Cmd/Ctrl+Enter also sends for
    // muscle memory.
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  // Starter chip click — fills the composer with the chip's prompt then
  // delegates to sendMessage() so every per-pane behavior (model + system
  // prompt overrides) is reused for free.
  sendStarterPrompt(event) {
    if (event) event.preventDefault()
    if (!this.hasInputTarget) return
    const prompt = event?.currentTarget?.dataset?.prompt
    if (!prompt) return
    this.inputTarget.value = prompt
    this.sendMessage()
  }

  // A `:briefing` playbook (today: "My day"). Deliberately NOT a pre-typed
  // message: the server composes the answer from the same Operations the MCP
  // tools use and broadcasts real cards, with no model call at all. That is
  // what makes it deterministic and, more to the point, clickable — the LLM's
  // version of this answer was prose in which nothing could be clicked.
  //
  // A `:prompt` playbook does NOT come through here; it is an ordinary
  // sendStarterPrompt.
  runPlaybook(event) {
    if (event) event.preventDefault()
    const playbookId = event?.params?.playbookId
    if (!playbookId) return

    // Same disconnected guard as #sendUserMessage: without it the user gets a
    // bubble and a spinner that can never be answered.
    if (!this.isConnected) {
      this.setError(this.notConnectedMessageValue)
      return
    }

    // Echo the user's choice as a message bubble, exactly as the ordinary send
    // path does — the server broadcasts no echo, and without this the
    // transcript would open with an answer to a question nobody appears to
    // have asked. The label is our own server-rendered i18n text, and
    // appendTextBubble is text-safe regardless. The echo is tagged
    // playbooks-pending: a REFUSED playbook (generic error frame) persists
    // nothing server-side, so onError drops the row — otherwise a turn that
    // never happened would ride #historyForServer() as a fabricated user
    // message and diverge from the server's own transcript.
    const label = event?.params?.playbookLabel
    if (label) {
      const echoBubble = this.appendTextBubble(
        "user",
        label,
        crypto.randomUUID(),
      )
      echoBubble
        ?.closest("[data-companion-message-row]")
        ?.setAttribute("data-playbook-echo", "pending")
    }

    this.setLoading(true)
    this.#perform("run_playbook", { playbook_id: playbookId })
  }

  // ──────────────────────────────────────────────────────────────────────
  // Clear conversation (workstream 3 sub-phase 3). Clears the LOCAL view
  // only — mirrors the React reference's clearMessages() (use-ai-chat.ts),
  // which is a client-side reset with no server call; the underlying
  // AIChat's persisted messages are untouched (a page refresh replays them
  // again via the `history` event). Same confirm-dialog pattern as the
  // chat-list rail's delete confirm (ui--modal + native <dialog>).

  handleClearClick() {
    this.#clearModalController()?.open()
  }

  handleClearCancel() {
    this.#clearModalController()?.close()
  }

  handleClearConfirm() {
    this.#clearModalController()?.close()
    // Clear does not stop the turn: the rest of the answer keeps arriving.
    // Drop the stream (and steps line) bound to the transcript we are about
    // to empty, or #ensureStream hands back a detached bubble and every
    // later chunk is written into it. The seq window stays: the turn is
    // still the one in flight, and Stop reads it (_stoppedTurn, seen seq).
    // A reconnect replay (onTurnInFlight) must not redraw what the user just
    // cleared: drop the buffered chunks and remember how much of the turn's
    // text is gone, since the server snapshot still carries it.
    this._clearedText += this._stream?.raw ?? ""
    this.#dropStream()
    this._turnChunks = []
    this._steps = null
    if (this.hasMessagesTarget) this.messagesTarget.innerHTML = ""
    this.#finalizeStall()
    this.streamingMessageId = null
    this.#clearAllTimers()
    this.#syncTranscriptState()
    this.inputTarget?.focus()
  }

  #clearModalController() {
    if (!this.hasClearDialogTarget) return null
    return this.application?.getControllerForElementAndIdentifier(
      this.clearDialogTarget,
      "ui--modal",
    )
  }

  // ──────────────────────────────────────────────────────────────────────
  // Card actions (action_confirmation Confirm/Cancel — CONEK-5188's
  // channel-side round-trip existed but nothing on the client ever called
  // it; the ADR's "dead confirmation buttons" defect).

  handleActionAccept(event) {
    const card = event.currentTarget.closest(
      '[data-testid="ai-action-confirmation"]',
    )
    // Double-submit guard: without this, a second click (or a slow network
    // before the card visually updates) fires execute_confirmed_tool twice
    // over the cable — AIChatChannel#execute_confirmed_tool has no
    // idempotency check of its own, so this can create two contacts/tasks/
    // tickets/quotes from one user action.
    if (card?.getAttribute("data-ai--companion-pending") === "true") return

    const { actionName: toolName, messageId, payload } = event.params
    if (!toolName) return
    // Stimulus casts a `-param` attribute through JSON.parse itself when the
    // value parses cleanly, so `payload` here is already the object — NOT a
    // string to parse again. A malformed attribute value falls back to the
    // raw string instead, which we treat the same as "no arguments" rather
    // than silently doing nothing; the server re-validates against the
    // tool's schema and will reject anything genuinely required.
    const toolArguments = payload && typeof payload === "object" ? payload : {}
    card?.setAttribute("data-ai--companion-pending", "true")
    card
      ?.querySelectorAll("button")
      .forEach((button) => (button.disabled = true))
    // messageId round-trips through AIChatChannel#execute_confirmed_tool onto
    // a failure's `error` broadcast, so onError can recover ONLY this card.
    this.#perform("execute_confirmed_tool", {
      tool_name: toolName,
      message_id: messageId,
      arguments: toolArguments,
    })
  }

  handleActionReject(event) {
    const { actionName, messageId } = event.params
    this.#perform("action_rejected", {
      action_name: actionName,
      message_id: messageId,
    })
    event.currentTarget
      .closest('[data-testid="ai-action-confirmation"]')
      ?.remove()
  }

  // ──────────────────────────────────────────────────────────────────────
  // Wire event dispatch

  handleMessage(data) {
    if (!data || typeof data !== "object") return
    if (typeof data.seq === "number") {
      this.#handleSequenced(data)
      return
    }
    this.#dispatchMessage(data)
  }

  // Reorder gate for `seq`-stamped frames. The window is scoped to the
  // producer's `turn` id: two producers can interleave on one channel (a
  // previous turn's late error frame landing mid-stream of the next turn —
  // exactly what a failing real-agent turn does while /mock streams), and
  // without the turn key the newcomer's seq 1 would reset the active
  // window and strand the in-flight frames in _seqPending until the
  // safety flush, splitting the transcript.
  #handleSequenced(data) {
    const turn = data.turn ?? null
    if (
      turn !== this._seqTurn ||
      this._seqExpected == null ||
      (turn === null && data.seq === 1)
    ) {
      this.#resetSeqState()
      this._seqTurn = turn
      this._seqExpected = data.seq
    }
    if (data.seq > this._seqExpected) {
      this._seqPending.set(data.seq, data)
      // Safety valve: if the missing frame never shows up (shouldn't happen
      // — cable delivery is reliable in-process), flush what we have in
      // order rather than sitting on it forever.
      this._seqFlushTimer ??= setTimeout(() => this.#flushSeqPending(), 1500)
      return
    }
    // seq < expected within the SAME turn = a duplicate delivery (Solid
    // Cable redelivers around a resubscribe — e.g. when the draft chat is
    // lazily persisted mid-turn). DROP it: re-dispatching a duplicated
    // `thinking` finalizes the in-flight bubble and splits the answer in
    // two; a duplicated chunk doubles the text.
    if (data.seq < this._seqExpected) return
    this.#dispatchMessage(data)
    this._seqExpected = data.seq + 1
    while (this._seqPending.has(this._seqExpected)) {
      const next = this._seqPending.get(this._seqExpected)
      this._seqPending.delete(this._seqExpected)
      this.#dispatchMessage(next)
      this._seqExpected += 1
    }
    if (this._seqPending.size === 0 && this._seqFlushTimer) {
      clearTimeout(this._seqFlushTimer)
      this._seqFlushTimer = null
    }
  }

  #flushSeqPending() {
    this._seqFlushTimer = null
    const backlog = [...this._seqPending.entries()].sort((a, b) => a[0] - b[0])
    this._seqPending.clear()
    for (const [seq, frame] of backlog) {
      this.#dispatchMessage(frame)
      this._seqExpected = seq + 1
    }
  }

  #resetSeqState() {
    this._seqTurn = null
    this._seqExpected = null
    this._seqPending.clear()
    this._turnChunks = []
    this._clearedText = ""
    if (this._seqFlushTimer) {
      clearTimeout(this._seqFlushTimer)
      this._seqFlushTimer = null
    }
  }

  #dispatchMessage(data) {
    if (this.playgroundModeValue) {
      this.forwardToInspector(data)
      this.trackLatency(data)
    }
    if (data.turn != null && data.type === "thinking")
      this._awaitingTurn = false
    if (this.#belongsToStoppedTurn(data)) return
    this.#settleStalledTurn(data)
    switch (data.type) {
      case "connected":
        this.onConnected(data)
        break
      case "history":
        this.onHistory(data)
        break
      case "tool_call_start":
        this.onToolCallStart(data)
        break
      case "tool_call_end":
        this.onToolCallEnd(data)
        break
      case "action_resolved":
        this.onActionResolved(data)
        break
      case "thinking":
        this.onThinking()
        break
      case "turn_in_flight":
        this.onTurnInFlight(data)
        break
      case "response":
        this.onResponse(data)
        break
      case "ui_component":
        this.onUiComponent(data)
        break
      case "navigation":
        this.onNavigation(data)
        break
      case "tool_result":
        this.onToolResult(data)
        break
      case "record_changed":
        this.onRecordChanged(data)
        break
      case "undo_result":
        this.onUndoResult(data)
        break
      case "suggestions":
        this.onSuggestions(data)
        break
      case "done":
        this.onDone()
        break
      case "error":
        this.onError(data)
        break
      default:
        // Unknown wire event — silent no-op (server may add new types
        // before client catches up; the chat must keep working).
        break
    }
  }

  // True for a frame of the turn the user stopped (CONEK-6946). The stop
  // already settled the UI; what the server sends before its `done` would
  // otherwise land as an orphan bubble (late chunks) or a card under the
  // Retry (a tool that was still running). The turn's `done`/`error` closes
  // the window and is swallowed too — the stop already did everything it does.
  #belongsToStoppedTurn(data) {
    const stopped = this._stoppedTurn
    if (stopped == null) return false

    if (data.turn == null) {
      // The channel's own frames carry no turn. Its `thinking` ack of the
      // message that was stopped before it arrived is stale; its `error`
      // means the turn never reached the agent, so nothing more is coming.
      if (stopped !== STOP_PENDING) return false
      if (data.type === "thinking") return true
      if (data.type === "error") this._stoppedTurn = null
      return false
    }

    if (stopped === STOP_PENDING) this._stoppedTurn = data.turn
    if (data.turn !== this._stoppedTurn) return false
    if (data.type === "done" || data.type === "error") this._stoppedTurn = null
    return true
  }

  // ──────────────────────────────────────────────────────────────────────
  // Handlers

  onConnected(data) {
    // Server transmits {ai_chat_id, name} — on subscribe for an existing
    // chat, or again when a draft chat is lazily persisted on its first
    // message (ai_chat_id was null before). Update the local chat-id so
    // subsequent sends + reconnects route correctly.
    if (!data.ai_chat_id) return
    const hadNoChat = !this.chatIdValue
    this.chatIdValue = data.ai_chat_id
    // A chat id arriving where we had none = the draft just became a real
    // chat. Persist the selection cookie and tell the rail to refresh so
    // the new thread shows up selected in the list.
    if (hadNoChat) {
      this.#syncChatUrl(data.ai_chat_id)
      this.#updateMaximizeLink(data.ai_chat_id)
      document.cookie = `companion_current_chat_id=${data.ai_chat_id}; path=/; SameSite=Lax`
      window.dispatchEvent(
        new CustomEvent("companion:chat-created", {
          detail: { chatId: data.ai_chat_id },
        }),
      )
    }
  }

  onHistory(data) {
    // Suggestions belong to the turn that produced them, not to the chat
    // being loaded.
    this.#clearComposerSuggestions()
    // Full replay on (re)subscribe — REPLACE the transcript, not append
    // (workstream 3 sub-phase 2). The user just switched chats or the
    // connection was re-established; the previous DOM (if any) is stale
    // and appending on top of it would duplicate the whole conversation.
    // Mirrors the React reference's `setMessages(entries.map(...))`.
    if (!this.hasMessagesTarget || !Array.isArray(data.messages)) return
    // The transcript is being replaced wholesale — an in-flight reveal
    // belongs to DOM we are about to delete.
    this.#dropStream()
    this.#resetSeqState()
    // The server-rendered skeleton (if any) goes with the rest of the DOM;
    // its safety timer must not fire into the replayed transcript.
    this.#clearSkeletonTimeout()
    this.messagesTarget.innerHTML = ""
    this.#finalizeStall()
    this.streamingMessageId = null
    this.#clearAllTimers()
    this._steps = null
    this._pinned = true
    this.#hideJumpToLatest()
    // ai.companion_ux_refresh — one folded steps line per turn, rebuilt from
    // the `tools` every assistant row carries in its context (the calls made
    // since the previous row, so before that row's text). `kind: "steps"` is
    // a card-only turn's row, which has nothing else to show.
    //
    // Where the line goes: live, it opens at the turn's first tool call —
    // after any sentence the model said first, above every card the tools
    // drew. The rows carrying the tools come back AFTER those cards (a round's
    // row is written once its text is final), so the line is placed after the
    // turn's `anchor` instead: its question, or a sentence that came before
    // any card.
    let replaySteps = null
    let anchor = null
    let cardSeen = false
    for (const msg of data.messages) {
      if (msg.role === "user") {
        replaySteps = null
        cardSeen = false
      }
      const tools = Array.isArray(msg.context?.tools) ? msg.context.tools : []
      if (this.uxRefreshValue && msg.role === "assistant" && tools.length) {
        replaySteps = this.#replaySteps(replaySteps, tools, anchor)
      }
      if (msg.kind === "steps") continue
      // Card replay (ai.companion_card_replay) — a persisted generative-UI
      // card comes back as a server-rendered fragment. Inserted here, in
      // sequence with the bubbles around it; see #appendCardFragment.
      if (msg.kind === "card") {
        this.#appendCardFragment(msg.html)
        cardSeen = true
        continue
      }
      // `instant: true` — a 50-message replay must not play 50 staggered
      // entrance animations; only genuinely new bubbles animate in.
      const bubble = this.appendTextBubble(msg.role, msg.content, msg.id, {
        instant: true,
      })
      if (msg.role === "user" || (!replaySteps && !cardSeen)) {
        anchor = bubble.closest("[data-companion-message-row]")
      }
      // CONEK-6946 — a stopped turn comes back stopped: the server persists
      // the interruption, so a reload or a cable re-render rebuilds the same
      // marker + Retry the Stop click drew.
      if (msg.interrupted && msg.role === "assistant" && msg.id) {
        this.#markAssistantMessageInterrupted(msg.id)
      }
    }
    // Covers the empty-history case too (loop never ran, but the transcript
    // was just cleared above) — createBubble already synced the non-empty case.
    this.#syncTranscriptState()
    this.#syncRegenerate()
  }

  // A replayed card (history frame, `kind: "card"`). The html is the same
  // server-rendered ViewComponent markup the live `ui_component` path hands
  // to Turbo.renderStreamMessage, minus the turbo-stream wrapper — so, like
  // that path, it is our own trusted markup, never model or page data.
  //
  // Inserted SYNCHRONOUSLY via a <template>, not through Turbo: stream
  // rendering is asynchronous (it awaits the next animation frame), so a
  // card replayed that way would land after every text bubble that follows
  // it in the loop above and the transcript would come back reordered.
  // `animation: none` opts out of the entrance slide like `createBubble`
  // does for replayed bubbles; #historyForServer reads `[data-role]` only,
  // so the card never leaks into the history sent to the model.
  #appendCardFragment(html) {
    if (!html || !this.hasMessagesTarget) return
    const template = document.createElement("template")
    template.innerHTML = html
    const root = template.content.firstElementChild
    if (!root) return
    root.style.animation = "none"
    this.messagesTarget.appendChild(root)
  }

  onToolCallStart(data) {
    // The server restarts its turn snapshot with each tool round (TurnState),
    // so the wiped prefix is per round, not per turn.
    this._clearedText = ""
    // A confirmed write's step (it names its card) — only the steps line
    // shows it; with the flag off the confirm stays as it always was. The
    // turn is not reopened: the write cannot be stopped once dispatched, so
    // no loading row and no Stop.
    if (data.message_id) {
      if (this.uxRefreshValue) this.#startStep(data)
      return
    }
    // A tool call ends the current block of prose by definition: the model
    // said something, went to fetch data, and whatever it says next is a new
    // block written with that data in hand.
    //
    // Finalizing here fixes two symptoms of the same cause. The tool loop runs
    // several streaming rounds per turn but `thinking` (the other place that
    // closes a bubble) fires only ONCE, at the start. So without this the
    // rounds shared one bubble, and:
    //
    //   - their text ran together with no break, since the model emits no
    //     separator between what are, to it, two separate messages
    //     ("...in progress or review:You currently have...")
    //   - any card the tool rendered was appended AFTER that shared bubble,
    //     so a search card showed up below the answer that already discussed
    //     its results, rather than above it in the order things happened
    //
    // `instant: true` skips whatever the pacing engine had left to reveal:
    // holding text back now would reorder it against the card.
    //
    // A round that streamed only whitespace before the tool (models do emit a
    // lone " " or "\n") leaves no prose, just an empty grey pill above the
    // card — which the replay never shows (blank rows are skipped), so the
    // live transcript and the reloaded one disagreed. Drop it instead.
    if (this._stream && this._stream.raw.trim() === "") {
      this.#discardStreamingBubble()
    }
    this.#finalizeStream({ instant: true })

    // Cancel the finished bubble's stall timer, and deliberately do NOT arm
    // one for the next round. A tool call plus the model's next round
    // routinely exceeds the 10s ACK_TIMEOUT_MS budget (measured gaps of 5s to
    // 18s), so a timer armed here marks a perfectly healthy turn as
    // stalled and shows a Retry button mid-answer. `onResponse` re-arms it
    // on every chunk, which is the right place: only silence while text is
    // expected is a stall, and waiting on a tool is not silence.
    this.#clearStallTimeout(this.streamingMessageId)

    // A FRESH id, not null: `findStreamingBubble` keys off this, so null would
    // make the next round's bubble anonymous.
    this.streamingMessageId = crypto.randomUUID()
    this.setLoading(true, this.#toolActivityLabel(data.tool_name))
    if (this.uxRefreshValue) this.#startStep(data)
  }

  // ai.companion_ux_refresh — the step that started with the matching
  // `tool_call_start` ended. Without the refresh the frame is ignored.
  onToolCallEnd(data) {
    if (this.uxRefreshValue) this.#endStep(data)
  }

  onThinking() {
    this.#clearComposerSuggestions()
    // If a previous assistant bubble is still streaming (e.g. the agent
    // produced a partial response, then called a tool, and is about to
    // produce another response), finalize that bubble's markdown NOW —
    // instantly, skipping whatever the pacing engine hadn't revealed yet.
    // Otherwise `onDone()` only parses the LAST bubble and earlier ones
    // remain visible as raw markdown (**bold**, tables, ---).
    this.#finalizeStream({ instant: true })
    // `thinking` = the server acked the most recently sent user message —
    // clear its timeout so it isn't later marked failed.
    if (this.lastSentUserMessageId) {
      this.#clearAckTimeout(this.lastSentUserMessageId)
      this.#setBubbleState(this.lastSentUserMessageId, "sent")
      this.lastSentUserMessageId = null
    }
    // Workstream 3 sub-phase 3 — a new turn starting successfully means any
    // earlier error is stale; the banner previously never cleared itself.
    this.setError("")
    // Every turn acks TWICE — the channel transmits `thinking` immediately
    // (CONEK-6182), then the agent's initialize_session broadcasts its own.
    // The second ack supersedes the first: the id minted below is the one the
    // answer will stream into, so the previous id's stall timer must die here
    // or it fires 45s later into #markAssistantMessageStalled with no
    // bubble to mark — and creates an empty "Retry" bubble under a perfectly
    // good answer.
    if (this.streamingMessageId) {
      this.#clearStallTimeout(this.streamingMessageId)
    }
    this.streamingMessageId = crypto.randomUUID()
    // FIRST_OUTPUT_TIMEOUT_MS, not ACK_TIMEOUT_MS: the turn's first output may
    // be a tool call rather than a chunk, and deciding to call one takes
    // longer than first-chunk latency. `onResponse` re-arms at the tighter
    // ACK_TIMEOUT_MS once text is actually flowing, and `onToolCallStart`
    // clears it outright. This timer only catches a turn that acked and then
    // produced NOTHING at all.
    this.#scheduleStallTimeout(
      this.streamingMessageId,
      this.constructor.FIRST_OUTPUT_TIMEOUT_MS,
    )
    this.setLoading(true, this.thinkingLabelValue)
  }

  // The channel's replay of a turn that was already running when this
  // subscription opened (the user switched chats and came back, or navigated
  // on a Hotwire page): `{turn, last_seq, content, tool_name}` — the agent's
  // turn id, the last seq the server saw, the text streamed so far, the tool
  // in flight. Deliberately NOT a `seq` field: this is the channel's frame,
  // it must bypass the reorder gate (which would drop it as "already seen"),
  // and it can land before OR after live frames of the same turn.
  //
  // Resume = indicator back on, one bubble holding the snapshot text plus any
  // live chunk that raced ahead of the snapshot (seq > snapshot seq — those
  // are not in `content`, and dropping them would lose text; re-adding chunks
  // ≤ seq would double it), and the seq window positioned at seq + 1 so the
  // next live frame continues the same bubble and older duplicates are dropped.
  onTurnInFlight(data) {
    const turn = data.turn ?? null
    const snapshotSeq = typeof data.last_seq === "number" ? data.last_seq : 0
    const sameTurn = turn !== null && this._seqTurn === turn
    const ahead = sameTurn
      ? this._turnChunks.filter((c) => c.seq > snapshotSeq)
      : []

    // Whatever early live frames drew is rebuilt below from snapshot + ahead.
    this.#discardStreamingBubble()
    this.onThinking()

    const content = data.content ?? ""
    const snapshot =
      sameTurn && content.startsWith(this._clearedText)
        ? content.slice(this._clearedText.length)
        : content
    const text = snapshot + ahead.map((c) => c.content).join("")
    if (text) {
      const stream = this.#ensureStream()
      stream.raw += text
      stream.handle.pushChunk(text)
      this.#scheduleStallTimeout(this.streamingMessageId)
    }
    if (data.tool_name && ahead.length === 0) {
      this.setLoading(true, this.#toolActivityLabel(data.tool_name))
    }

    if (!sameTurn) {
      this.#resetSeqState()
      this._seqTurn = turn
      this._seqExpected = snapshotSeq + 1
    }

    // CONEK-9352 — the history replay that came just before this frame ran
    // with no turn in flight, so it offered Regenerate on the last finished
    // answer. A resumed turn means that answer is no longer the last word:
    // hide the button (onThinking sets streamingMessageId but does not sync).
    this.#syncRegenerate()
  }

  // Cancels the pacing engine and forgets the in-flight bubble, leaving its
  // DOM and the seq window to the caller: every site that empties or replaces
  // the transcript decides those for itself.
  #dropStream() {
    this._stream?.handle.cancel()
    this._stream = null
  }

  // Drops the in-flight assistant bubble and its pacing engine — used when a
  // snapshot is about to redraw the turn from authoritative server state.
  #discardStreamingBubble() {
    const stream = this._stream
    if (!stream) return
    stream.handle.cancel()
    stream.bubble.closest("[data-companion-message-row]")?.remove()
    this._stream = null
    this.#syncTranscriptState()
  }

  // Stop button (loading row). Asks the channel to cancel the turn in flight
  // and settles the UI right away: what streamed stays (finalized as
  // markdown), the indicator goes, no timer is left to mark anything failed.
  // The agent's `done` arrives a moment later and is idempotent on all of it.
  stopGeneration() {
    const loadingVisible =
      this.hasLoadingTarget && !this.loadingTarget.classList.contains("hidden")
    if (!loadingVisible && !this.streamingMessageId) return

    // Capture the id BEFORE the teardown nulls it — it is what the
    // interrupted-state marking below addresses.
    const stoppedId = this.streamingMessageId
    // Frames of this turn still to come are dropped (#belongsToStoppedTurn).
    this._stoppedTurn = this._awaitingTurn ? STOP_PENDING : this._seqTurn
    // Stopping the stalled turn settles its stall (CONEK-7585). Before the
    // agent names the turn, an unnamed stall is the stopped one's: both wait
    // for the same next turn. A stall of another turn still waits for that
    // turn's own frames.
    const stallIsStoppedTurn = this._awaitingTurn
      ? this._stalledTurn == null
      : this._stalledTurn === this._seqTurn
    if (stallIsStoppedTurn) this.#finalizeStall()
    // The ack may never be dispatched now (it is one of the dropped frames),
    // and #clearAllTimers below forgets which bubble it was for.
    if (this.lastSentUserMessageId) {
      this.#setBubbleState(this.lastSentUserMessageId, "sent")
    }

    // The last frame this client rendered. Everything after it is dropped
    // here, so the server leaves it out of what it persists too — a reload
    // then shows exactly what the user saw when they stopped (CONEK-6946).
    const seenSeq = this._awaitingTurn
      ? 0
      : Math.max((this._seqExpected ?? 1) - 1, 0)
    this.#perform("stop_generation", { seq: seenSeq })
    // Same rule as #onToolCallStart: a round that streamed only whitespace
    // is no prose. The server persists it as nothing (and marks the round's
    // sentence instead), so the live bubble must not keep it either.
    if (this._stream && this._stream.raw.trim() === "") {
      this.#discardStreamingBubble()
    }
    this.#finalizeStream({ instant: true })
    this.#clearAllTimers()
    // A manual stop is an INTERRUPTED turn, not a completed one. Until this,
    // the only route to that state (and so to the Retry CTA) was the stall
    // timer — which #clearAllTimers() has just destroyed — so a stopped turn
    // settled looking finished and the user's question was gone, with nothing
    // to press. #markAssistantMessageInterrupted creates the (empty) bubble
    // itself when the stop landed before the first chunk, and walks back to
    // the triggering user turn for the Retry param, so both cases are covered
    // by the one call. `done` arrives after and leaves data-state alone.
    if (stoppedId) this.#markAssistantMessageInterrupted(stoppedId)
    this.streamingMessageId = null
    this.setLoading(false)
    this.#finishSteps()
    this._turnRunning = false
    this.#syncRegenerate()
  }

  // Lazily builds the streaming render state for the current assistant turn.
  // The text-stream engine paces the character reveal (same engine as the
  // deleted React hook — see ../../lib/text-stream), while `raw` keeps the
  // exact chunk text so the final markdown parse never depends on how far
  // the animation got.
  #ensureStream() {
    if (this._stream) return this._stream
    const bubble =
      this.findStreamingBubble() ??
      this.createBubble("assistant", this.streamingMessageId)
    // Prose styles apply from the FIRST chunk — the reveal below renders
    // markdown progressively, so headings/tables/lists must be styled while
    // they stream in, not only after `done`.
    bubble.classList.add("prose", "prose-sm", "dark:prose-invert", "max-w-none")
    const stream = { bubble, raw: "", handle: null, finalized: false }
    stream.handle = createTextStream({
      target: (rendered) => {
        // Progressive markdown: parse the revealed prefix on every tick so
        // `## headings`, **bold** and tables render live instead of showing
        // raw markdown until the turn completes. renderMarkdown sanitises
        // (DOMPurify) — same pipeline as the final render. An unterminated
        // token (`**bo`) renders literally for a frame or two and heals as
        // its closing marker streams in; the payloads here are chat-sized,
        // so a parse per animation frame is comfortably under budget.
        this.#renderBubbleMarkdown(bubble, rendered)
        // Follow the growing text, but never fight the user: if they
        // scrolled up to read something, stay put.
        this.scrollToBottom({ force: false })
      },
      onStart: () => bubble.classList.add("is-streaming"),
      onComplete: () => this.#finalizeStream(),
    })
    this._stream = stream
    return stream
  }

  // CONEK-7585 — renders `raw` as the bubble's markdown, keeping a marker label
  // and Retry under it. A stall marks the bubble while its stream is still
  // open: the reveal is often still drawing the text, and the turn's `error`
  // finalizes the stream with the marker inside it.
  #renderBubbleMarkdown(bubble, raw) {
    const markers = bubble.querySelectorAll(
      "[data-companion-marker-label], [data-companion-retry]",
    )
    bubble.innerHTML = renderMarkdown(raw)
    bubble.append(...markers)
  }

  // Swap the streamed plain text for the parsed markdown once the turn is
  // over. `instant: true` (tool-call handoff, error, chat switch) cancels the
  // pacing engine and renders the full text immediately; the default path is
  // reached via the engine's onComplete after the reveal finishes draining.
  #finalizeStream({ instant = false } = {}) {
    const stream = this._stream
    if (!stream || stream.finalized) return
    stream.finalized = true
    this._stream = null
    if (instant) stream.handle.cancel()
    const { bubble, raw } = stream
    bubble.classList.remove("is-streaming")
    bubble.setAttribute("aria-busy", "false")
    if (raw.length > 0) {
      this.#renderBubbleMarkdown(bubble, raw)
      bubble.classList.add(
        "prose",
        "prose-sm",
        "dark:prose-invert",
        "max-w-none",
      )
      this.#attachMessageActions(bubble, raw)
    }
    this.scrollToBottom({ force: false })
    // `done` often lands while the reveal is still draining, so the answer
    // only becomes the last finished one here.
    this.#syncRegenerate()
  }

  onResponse(data) {
    if (!this.hasMessagesTarget) return
    if (!data.content) return
    if (typeof data.seq === "number") {
      this._turnChunks.push({ seq: data.seq, content: data.content })
    }
    const stream = this.#ensureStream()
    stream.raw += data.content
    stream.handle.pushChunk(data.content)
    // Each chunk resets the stall window — only SILENCE for the full
    // window counts as a stall, not total response time.
    if (this.streamingMessageId) {
      this.#scheduleStallTimeout(this.streamingMessageId)
    }
  }

  onUiComponent(data) {
    // GA programme F2.2 — canvas bridge. Three `ui_component` broadcasts are
    // NOT cards: they are instructions for the drawing editor's PIXI canvas.
    // `drawing_tools.rb` emits them with `data:` and deliberately no `html:`
    // (AI::CompanionCardRenderer has no component for them), so the
    // no-html guard below dropped them with a console warning — AI drawing
    // generation was silently dead on this renderer, while React's
    // use-ai-chat.ts translated them into window events. Do this BEFORE the
    // guard, and before the messages-target check: the canvas listener
    // (use-ai-drawing-placement.ts) is what consumes them, not the transcript.
    if (this.#bridgeCanvasComponent(data)) return

    if (!this.hasMessagesTarget) return
    if (!data.html) {
      console.warn(
        `[ai--companion] ui_component "${data.component}" had no rendered html — skipping`,
      )
      return
    }
    Turbo.renderStreamMessage(data.html)
    // renderStreamMessage lands the card asynchronously — scroll on the next
    // frame so the smooth scroll targets the grown scrollHeight.
    requestAnimationFrame(() => {
      this.scrollToBottom({ smooth: true })
      this.#syncTranscriptState()
      if (this.uxRefreshValue && data.component === "action_confirmation") {
        this.#armConfirmationCard()
      }
    })
  }

  // Re-dispatches a canvas-bound `ui_component` as the window CustomEvent the
  // drawing editor listens for, and reports whether it handled the payload.
  // Consumer: app/frontend/domains/orders/components/drawings/editor/
  // ai-generation/use-ai-drawing-placement.ts (three window listeners).
  // Producer: packs/ai/app/agents/companion_agent/drawing_tools.rb.
  // Keep CANVAS_BRIDGE_EVENTS in sync with that hook's listener names — a
  // rename on either side silently stops products landing on the canvas.
  #bridgeCanvasComponent(data) {
    const eventName = CANVAS_BRIDGE_EVENTS[data.component]
    if (!eventName) return false
    // `data.data` is the tool's result payload. Absent means the broadcast is
    // malformed; don't dispatch an event whose detail the canvas will index
    // into — a silent no-op beats a TypeError inside the PIXI handler.
    if (!data.data) {
      console.warn(
        `[ai--companion] ${data.component} arrived with no data — skipping canvas bridge`,
      )
      return true
    }
    window.dispatchEvent(new CustomEvent(eventName, { detail: data.data }))
    return true
  }

  // The single navigation seam for the panel: every card row / CTA / context
  // chip click (#navigateToPath) and the agent's `navigation` frames land here.
  //
  // This used to be `window.location.assign(url)` on an un-prefixed path —
  // a full document reload (React reboot, this panel remounted empty until
  // the cable replayed the thread), and TWICE, because `/tasks/<id>` first
  // 302s through SpaceRedirectsController onto the space prefix. That was
  // the "white flash + the chat is gone" report.
  //
  // Now: (1) the space prefix is applied client-side so the hop disappears;
  // (2) the navigation is announced as a cancelable `companion:navigate`
  // window event. On Inertia pages the React app claims it
  // (app/frontend/shared/lib/companion-navigation.ts → router.visit) and
  // this panel — a sibling of the Inertia root, not a child — is never
  // touched: cable up, transcript intact, no flash. When nothing claims it
  // (Hotwire surfaces, or a Hotwire-served target) the full navigation is
  // still the right and only safe move: a Turbo visit that swapped <body>
  // under this controller's live subscription would half tear it down.
  onNavigation(data) {
    if (!data.url) return
    // Defence-in-depth: reject non-http(s) and non-relative URLs to block
    // `javascript:` / `data:` schemes if a compromised AI output ever lands
    // here. The cable broadcast is server-controlled today, but the cost is
    // one line.
    if (!/^(https?:|\/|\.?\/)/.test(data.url)) return
    const url = this.#withSpacePrefix(data.url)
    const entityType = data.entity_type ?? data.entityType ?? null
    const claimed = !window.dispatchEvent(
      new CustomEvent("companion:navigate", {
        cancelable: true,
        detail: { url, entityType },
      }),
    )
    if (claimed) return
    this.performFullNavigation(url)
  }

  // ai.companion_answer_style — the model's follow-ups
  // (suggest_next_steps), rendered server-side and trusted like every other
  // card fragment. Only the latest set is shown; the next turn clears it.
  onSuggestions(data) {
    if (!this.hasComposerSuggestionsTarget || !data.html) return
    this.composerSuggestionsTarget.innerHTML = data.html
    this.composerSuggestionsTarget.classList.remove("hidden")
  }

  // A chip FILLS the textarea rather than sending: the suggestion is a draft
  // the user may edit (compare sendStarterPrompt, which sends).
  fillSuggestion(event) {
    const prompt = event.currentTarget?.dataset?.prompt
    if (!prompt || !this.hasInputTarget) return
    this.inputTarget.value = prompt
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.focus()
    this.#clearComposerSuggestions()
  }

  #clearComposerSuggestions() {
    if (!this.hasComposerSuggestionsTarget) return
    this.composerSuggestionsTarget.innerHTML = ""
    this.composerSuggestionsTarget.classList.add("hidden")
  }

  // ai.companion_write_actions — the Undo button on a confirmed write's result chip.
  // The server decides (Operations::Companion::UndoWrite); the client only
  // keeps one request per chip in flight and reflects the answer.
  undoWrite(event) {
    const token = event.params?.undoToken
    if (!token || !this.subscription) return
    this._undoInFlight ??= new Set()
    if (this._undoInFlight.has(token)) return
    this._undoInFlight.add(token)
    // A new attempt supersedes the last one's error.
    this.setError("")
    if (event.currentTarget instanceof HTMLButtonElement)
      event.currentTarget.disabled = true
    this.subscription.perform("undo_write", { undo_token: token })
  }

  onUndoResult(data) {
    const token = data.undo_token
    this._undoInFlight?.delete(token)
    const button = [
      ...this.element.querySelectorAll("[data-ai--companion-undo-token-param]"),
    ].find(
      (el) => el.getAttribute("data-ai--companion-undo-token-param") === token,
    )
    if (data.status === "undone") {
      const chip = button?.closest('[data-testid="tool-result-chip"]')
      if (chip) chip.dataset.undone = "true"
      return
    }
    if (button) button.disabled = false
    if (data.message) this.setError(data.message)
  }

  // ai.companion_panel_v2 — a confirmed write changed a record; refresh
  // the page behind the panel so it stops showing stale data (demo
  // 2026-09-23). Same claim-or-fallback shape as onNavigation: announced as a
  // cancelable `companion:record-changed` window event, which the Inertia app
  // claims (companion-navigation.ts → router.reload + toast). Unclaimed, a
  // Hotwire page is morph-refreshed in place.
  onRecordChanged(data) {
    const ids = Array.isArray(data.entity_ids) ? data.entity_ids : []
    this._pendingRecordChange = {
      entityType: data.entity_type ?? null,
      entityIds: [...(this._pendingRecordChange?.entityIds ?? []), ...ids],
      action: data.action ?? "updated",
    }
    clearTimeout(this._recordChangedTimer)
    this._recordChangedTimer = setTimeout(
      () => this.#flushRecordChanged(),
      this.constructor.RECORD_CHANGED_COALESCE_MS,
    )
  }

  #flushRecordChanged() {
    this._recordChangedTimer = null
    const detail = this._pendingRecordChange
    this._pendingRecordChange = null
    if (!detail) return

    const claimed = !window.dispatchEvent(
      new CustomEvent("companion:record-changed", { cancelable: true, detail }),
    )
    if (claimed) return
    // The full-page chat has no page behind it to refresh.
    if (this.hasChatsPathValue && this.chatsPathValue) return

    // Never reload under a field the user is typing in — that would throw
    // away their input. Tell them instead.
    if (this.#userIsEditingPage()) {
      this.#showToast(
        this.hasStaleToastTemplateTarget ? this.staleToastTemplateTarget : null,
      )
      return
    }

    // A morph keeps everything that did not change, and `data-turbo-permanent`
    // (which needs an id) keeps this panel — transcript and live cable
    // subscription included — out of it entirely.
    // Only for THIS refresh: left on, it would also carry the panel across the
    // user's next Turbo visit, which is not how the panel behaves today.
    if (!this.element.id) this.element.id = "ai-companion-panel"
    this.element.setAttribute("data-turbo-permanent", "")
    document.addEventListener(
      "turbo:load",
      () => this.element.removeAttribute("data-turbo-permanent"),
      { once: true },
    )
    // After the morph, not before: the morph resets #toast-container to the
    // server render and would take a toast shown now with it.
    const template = this.hasRefreshedToastTemplateTarget
      ? this.refreshedToastTemplateTarget
      : null
    // "Here is what changed": the text that differs after the morph flashes.
    const before = snapshotTexts(document.body)
    document.addEventListener(
      "turbo:morph",
      () => {
        this.#showToast(template)
        highlightChanges(document.body, before)
      },
      { once: true },
    )
    Turbo.session.refresh(document.baseURI, {
      method: "morph",
      scroll: "preserve",
    })
  }

  #userIsEditingPage() {
    const el = document.activeElement
    if (!(el instanceof HTMLElement) || this.element.contains(el)) return false
    return el.isContentEditable || el.matches("input, textarea, select")
  }

  // Clones a server-rendered toast into the layout's #toast-container, where
  // ui--toast connects (animation + auto-dismiss) — the catalog tree
  // controller's pattern. A layout without a container just gets no toast.
  #showToast(template) {
    const container = document.getElementById("toast-container")
    const toast = template?.content?.firstElementChild?.cloneNode(true)
    if (!container || !toast) return
    toast.id = `toast-companion-${Math.random().toString(36).slice(2, 10)}`
    container.appendChild(toast)
  }

  // Kept as its own method so tests can intercept the one call jsdom cannot
  // observe (Location#assign is non-configurable there).
  performFullNavigation(url) {
    window.location.assign(url)
  }

  // `/tasks/<id>` → `/s:<group>:<branches>/tasks/<id>`, from the same
  // `window.currentSpace` every layout defines (and the chat-list rail already
  // reads for its frame URL). Absolute URLs, already-prefixed paths and
  // `/docs/*` (outside the space scope, resolved un-prefixed) pass through;
  // so does everything when the space is unknown, which is exactly the
  // redirect-based behaviour this replaces.
  #withSpacePrefix(url) {
    if (!url.startsWith("/")) return url
    if (url.startsWith("/s:") || url.startsWith("/docs")) return url
    const space = window.currentSpace
    if (!space?.groupKey || !space?.branchKeys) return url
    return `/s:${space.groupKey}:${space.branchKeys}${url}`
  }

  onToolResult(data) {
    if (!this.hasMessagesTarget) return
    if (!data.html) {
      console.warn(
        "[ai--companion] tool_result had no rendered html — skipping",
      )
      return
    }
    Turbo.renderStreamMessage(data.html)
    // renderStreamMessage lands the card asynchronously — scroll on the next
    // frame so the smooth scroll targets the grown scrollHeight.
    requestAnimationFrame(() => {
      this.scrollToBottom({ smooth: true })
      this.#syncTranscriptState()
    })
  }

  onDone() {
    // Let the pacing engine drain the remaining buffered text at reading
    // speed; #finalizeStream runs from its onComplete (or synchronously when
    // the buffer is already empty / reduced-motion is on). CONEK-3367's
    // aria-busy flip happens inside #finalizeStream.
    if (this._stream) {
      this._stream.handle.finish()
    }
    if (this.streamingMessageId)
      this.#clearStallTimeout(this.streamingMessageId)
    this.streamingMessageId = null
    this.setLoading(false)
    this.#finishSteps()
    this._turnRunning = false
    this.#syncRegenerate()
    // A playbook echo that reached its `done` frame is now a real, persisted
    // turn — un-tag it so a LATER, unrelated error frame can't sweep it away
    // (see onError's data-playbook-echo cleanup).
    this.messagesTarget
      ?.querySelectorAll('[data-playbook-echo="pending"]')
      .forEach((row) => row.removeAttribute("data-playbook-echo"))
  }

  onError(data) {
    // Whether a turn was running when this arrived — read before the teardown
    // below hides the indicator. Only such an error belongs to a turn.
    const turnWasRunning =
      Boolean(this.streamingMessageId) ||
      (this.hasLoadingTarget &&
        !this.loadingTarget.classList.contains("hidden"))
    // A partial answer may be mid-reveal — freeze it as markdown so the
    // transcript doesn't keep a blinking cursor next to the error banner.
    this.#finalizeStream({ instant: true })
    if (this.streamingMessageId)
      this.#clearStallTimeout(this.streamingMessageId)
    this.streamingMessageId = null
    this.setLoading(false)
    // Drop a playbook echo whose turn was refused (unknown id / wrong kind /
    // unpermitted / rate-limited): nothing was persisted server-side, so the
    // client-only "user turn" must not survive into #historyForServer() as a
    // fabricated conversation turn. An ordinary send's echo is server-
    // broadcast and confirmed, and never carries this tag.
    this.messagesTarget
      ?.querySelectorAll('[data-playbook-echo="pending"]')
      .forEach((row) => row.remove())
    // `data.message` is rendered VERBATIM on purpose, and this is the
    // documented exception to conek-i18n/no-server-error-fallback (see that
    // rule's header): the rule exists because a Rails interactor's `message`
    // is an untranslated developer diagnostic. AIChatChannel is the opposite —
    // every `error` frame it emits is already `I18n.t(...)`
    // (ai.admin.channel.chat_disabled / rate_limited / prompt_too_long /
    // tool_execution_failed / …), so it arrives in the user's own language.
    // The channel also sends NO `code`, so there is nothing to map. Replacing
    // it with a generic client-side string would DOWNGRADE the UX: "you are
    // sending messages too quickly" would become "an error occurred".
    // The only raw-string case is the admin `/call` escape hatch, which is
    // gated on ai.admin.playground.use and where the raw MCP error is the
    // point.
    this.#finishSteps()
    this._turnRunning = false
    // ai.companion_ux_refresh — a turn that failed says so in the transcript,
    // where it failed, with Retry next to it. An error that belongs to no turn
    // (a refused undo, a card action — those carry message_id) keeps the banner.
    // eslint-disable-next-line conek-i18n/no-server-error-fallback -- channel messages are localized server-side; see above
    const message = data.message || this.genericErrorMessageValue
    if (!(
      this.uxRefreshValue &&
      turnWasRunning &&
      !data.message_id &&
      this.#appendInlineError(message)
    )) {
      this.setError(message)
    }
    // `error` is the single broadcast type for EVERY failure on this channel
    // (chat_disabled, prompt_too_long, a bad slash-command, an unrelated LLM
    // turn failing, AND execute_confirmed_tool failing) — not just a pending
    // card's own execution. Recovering the pending card on every one of these
    // would defeat the double-submit guard: an unrelated error arriving while
    // a real write is still in flight (or has already SUCCEEDED and only a
    // downstream render step failed) would re-enable Confirm and let the user
    // fire the same tool_name/arguments a second time. Only recover when the
    // server echoes back the message_id of the card that actually triggered
    // this specific execute_confirmed_tool call (set below in
    // handleActionAccept, threaded through AIChatChannel#execute_confirmed_tool).
    if (data.message_id) this.#recoverPendingConfirmationCard(data.message_id)
  }

  #recoverPendingConfirmationCard(messageId) {
    const card = this.messagesTarget?.querySelector(
      `[data-testid="ai-action-confirmation"][data-message-id="${CSS.escape(messageId)}"][data-ai--companion-pending="true"]`,
    )
    if (!card) return
    card.removeAttribute("data-ai--companion-pending")
    card
      .querySelectorAll("button")
      .forEach((button) => (button.disabled = false))
  }

  // ──────────────────────────────────────────────────────────────────────
  // ai.companion_ux_refresh — steps line, confirmation, message actions,
  // jump-to-latest, inline turn errors. Every entry point checks
  // uxRefreshValue (or is only reachable from markup the flag renders), so
  // with the flag off none of this runs.

  #cloneTemplate(template) {
    return template.content.firstElementChild.cloneNode(true)
  }

  // A step started: open the turn's steps line if this is its first tool,
  // then add a running row. The line goes where the turn is — after the prose
  // the model said before calling the tool.
  #startStep(data) {
    if (!this.hasStepsTemplateTarget || !this.hasStepRowTemplateTarget) return
    // No step_id: plumbing the server keeps out of the line (STEPLESS_TOOLS).
    if (!data.step_id) return
    if (!this._steps) {
      // A confirmed write runs after its turn's `done`: its step joins that
      // turn's line above the card, as the replay counts it (CONEK-9024).
      const turnLine = data.message_id
        ? this.#confirmedTurnSteps(data.message_id)
        : null
      const el = turnLine ?? this.#cloneTemplate(this.stepsTemplateTarget)
      el.dataset.state = "running"
      this.#setStepsExpanded(el, true)
      // A card whose turn drew no line gets one right above it, never under
      // whatever turn came after it.
      const card =
        !turnLine && data.message_id
          ? this.#confirmationCard(data.message_id)
          : null
      if (card) card.before(el)
      else if (!turnLine) this.messagesTarget.appendChild(el)
      this._steps = { el, rows: new Map() }
      this.#syncTranscriptState()
    }
    const label = this.#toolActivityLabel(data.tool_name)
    const row = this.#buildStepRow("running", label, "")
    row.dataset.toolName = data.tool_name ?? ""
    this._steps.el.querySelector("[data-companion-steps-list]").appendChild(row)
    if (data.step_id) this._steps.rows.set(data.step_id, row)
    this.#setStepsSummary(this._steps.el, label)
    this.scrollToBottom({ force: false })
  }

  // The steps line of the turn that showed this confirmation card: the last
  // one before the card, and after the question that started that turn.
  #confirmationCard(messageId) {
    return this.messagesTarget.querySelector(
      `[data-testid="ai-action-confirmation"][data-message-id="${cssEscape(messageId)}"]`,
    )
  }

  #confirmedTurnSteps(messageId) {
    const card = this.#confirmationCard(messageId)
    if (!card) return null
    const before = (el) =>
      el.compareDocumentPosition(card) & Node.DOCUMENT_POSITION_FOLLOWING
    const question = [
      ...this.messagesTarget.querySelectorAll('[data-role="user"]'),
    ]
      .filter(before)
      .at(-1)
    const line = [
      ...this.messagesTarget.querySelectorAll("[data-companion-steps]"),
    ]
      .filter(before)
      .at(-1)
    if (!line) return null
    if (
      question &&
      !(
        question.compareDocumentPosition(line) &
        Node.DOCUMENT_POSITION_FOLLOWING
      )
    )
      return null
    return line
  }

  #endStep(data) {
    if (!this._steps) return
    const row =
      this._steps.rows.get(data.step_id) ??
      [
        ...this._steps.el.querySelectorAll(
          '[data-companion-step][data-state="running"]',
        ),
      ].find((el) => el.dataset.toolName === data.tool_name)
    if (!row) return
    const failed = data.status === "error"
    this.#fillStepRow(row, {
      state: failed ? "error" : "ok",
      label: failed
        ? this.toolActivityFailedValue
        : this.#toolDoneLabel(data.tool_name),
      ms: data.duration_ms,
    })
  }

  // The turn is over (done, error, Stop, or the next message): fold the line
  // to "Worked for 4.2s · 3 steps". A step still running never sent its end
  // (the tool raised, or Stop cut it off) — it settles without a duration.
  #finishSteps() {
    const steps = this._steps
    this._steps = null
    if (!steps) return
    steps.el
      .querySelectorAll('[data-companion-step][data-state="running"]')
      .forEach((row) =>
        this.#fillStepRow(row, {
          state: "ok",
          label: this.#toolDoneLabel(row.dataset.toolName),
        }),
      )
    this.#foldSteps(steps.el)
  }

  // History replay: the persisted `tools` ({name, status, ms}) of one row,
  // added to this turn's line (created folded on its first tools).
  #replaySteps(existing, tools, anchor) {
    if (!this.hasStepsTemplateTarget || !this.hasStepRowTemplateTarget)
      return existing
    let el = existing
    if (!el) {
      el = this.#cloneTemplate(this.stepsTemplateTarget)
      el.style.animation = "none"
      if (anchor?.isConnected) anchor.after(el)
      else this.messagesTarget.appendChild(el)
    }
    const list = el.querySelector("[data-companion-steps-list]")
    for (const tool of tools) {
      const failed = tool.status === "error"
      const row = this.#buildStepRow(
        failed ? "error" : "ok",
        failed ? this.toolActivityFailedValue : this.#toolDoneLabel(tool.name),
        this.#formatDuration(tool.ms),
      )
      row.dataset.durationMs = String(Number(tool.ms) || 0)
      list.appendChild(row)
    }
    this.#foldSteps(el)
    return el
  }

  #foldSteps(el) {
    const rows = el.querySelectorAll("[data-companion-step]")
    const totalMs = [...rows].reduce(
      (sum, row) => sum + (Number(row.dataset.durationMs) || 0),
      0,
    )
    el.dataset.state = "done"
    this.#setStepsExpanded(el, false)
    this.#setStepsSummary(
      el,
      this.#stepsSummaryText(rows.length, this.#formatDuration(totalMs)),
    )
  }

  #buildStepRow(state, label, duration) {
    const row = this.#cloneTemplate(this.stepRowTemplateTarget)
    row.dataset.state = state
    row.querySelector("[data-companion-step-label]").textContent = label
    row.querySelector("[data-companion-step-duration]").textContent = duration
    return row
  }

  #fillStepRow(row, { state, label, ms }) {
    row.dataset.state = state
    row.querySelector("[data-companion-step-label]").textContent = label
    if (typeof ms === "number") {
      row.dataset.durationMs = String(ms)
      row.querySelector("[data-companion-step-duration]").textContent =
        this.#formatDuration(ms)
    }
  }

  #setStepsSummary(el, text) {
    el.querySelector("[data-companion-steps-summary]").textContent = text
  }

  #setStepsExpanded(el, expanded) {
    el.dataset.expanded = String(expanded)
    el.querySelector("[data-companion-steps-toggle]")?.setAttribute(
      "aria-expanded",
      String(expanded),
    )
  }

  toggleSteps(event) {
    const el = event.currentTarget.closest("[data-companion-steps]")
    if (el) this.#setStepsExpanded(el, el.dataset.expanded !== "true")
  }

  // Past form of the activity label ("Searched your data"); a write the user
  // confirmed is named by its confirmation title.
  #toolDoneLabel(toolName) {
    if (
      this.hasToolActivityDoneLabelsValue &&
      this.toolActivityDoneLabelsValue[toolName]
    ) {
      return this.toolActivityDoneLabelsValue[toolName]
    }
    return this.toolActivityDoneFallbackValue
  }

  // "1.2s" under ten seconds, whole seconds above — StepsLineComponent's rule.
  // Same reading as StepsLineComponent.format_duration in the gem: under
  // 100 ms (zero included) is "<0.1s", not a "0s" that looks like nothing ran;
  // rounded before the format is picked, so 9,960 ms reads "10s".
  #formatDuration(ms) {
    const value = Math.max(Number(ms) || 0, 0)
    const format = new Intl.NumberFormat(this.localeValue || undefined)
    if (value < 100) return `<${format.format(0.1)}s`
    const tenths = Math.round(value / 100)
    return tenths < 100
      ? `${format.format(tenths / 10)}s`
      : `${format.format(Math.round(value / 1000))}s`
  }

  #stepsSummaryText(count, duration) {
    const forms = this.hasStepsSummaryValue ? this.stepsSummaryValue : {}
    let form = "other"
    try {
      form = new Intl.PluralRules(this.localeValue || undefined).select(count)
    } catch {
      // An unknown locale tag: "other" is the one form every locale has.
    }
    const template = forms[form] ?? forms.other ?? ""
    return template
      .replace("%{count}", String(count))
      .replace("%{duration}", duration)
  }

  // A confirmation card just landed. When the user is not in the middle of
  // typing, Confirm takes focus — Enter confirms, Esc cancels, and the card
  // shows those hints. Typing in the composer is never interrupted.
  #armConfirmationCard() {
    const cards = this.messagesTarget?.querySelectorAll(
      '[data-testid="ai-action-confirmation"]:not([data-resolved])',
    )
    const card = cards?.[cards.length - 1]
    if (!card || card.dataset.companionArmed) return
    card.dataset.companionArmed = "true"
    card.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return
      if (card.getAttribute("data-ai--companion-pending") === "true") return
      // The panel closes on Escape too (companion_sidebar_controller); here it
      // means "no" to this card, and only that.
      event.stopPropagation()
      event.preventDefault()
      card
        .querySelector(
          '[data-action~="click->ai--companion#handleActionReject"]',
        )
        ?.click()
      this.inputTarget?.focus()
    })
    const active = document.activeElement
    const composerBusy =
      this.hasInputTarget &&
      active === this.inputTarget &&
      this.inputTarget.value.trim() !== ""
    const focusElsewhere =
      active && active !== document.body && !this.element.contains(active)
    if (composerBusy || focusElsewhere) return
    card.dataset.kbd = "true"
    card.querySelector('[data-testid="ai-action-confirm"]')?.focus({
      preventScroll: true,
    })
  }

  // The confirmed write went through: the card stops asking. Its content is
  // swapped for the one-line "Confirmed" render it carries in a <template>.
  onActionResolved(data) {
    if (!this.uxRefreshValue || !data.message_id) return
    const card = this.messagesTarget?.querySelector(
      `[data-testid="ai-action-confirmation"][data-message-id="${cssEscape(data.message_id)}"]`,
    )
    const template = card?.querySelector(
      "template[data-companion-resolved-template]",
    )
    if (!card || !template) return
    const hadFocus = card.contains(document.activeElement)
    card.replaceChildren(template.content.cloneNode(true))
    card.dataset.resolved = "true"
    card.removeAttribute("data-ai--companion-pending")
    delete card.dataset.kbd
    if (hadFocus) this.inputTarget?.focus()
  }

  // Copy / Regenerate under a finished assistant answer. Appended next to
  // the bubble's row, never inside the bubble (#historyForServer reads it).
  #attachMessageActions(bubble, raw) {
    if (!this.uxRefreshValue || !this.hasMessageActionsTemplateTarget) return
    if (bubble.dataset.role !== "assistant") return
    this._rawByBubble.set(bubble, raw)
    const column = bubble.parentElement?.parentElement
    if (!column || column.querySelector("[data-companion-message-actions]"))
      return
    column.classList.add("group/message")
    column.appendChild(this.#cloneTemplate(this.messageActionsTemplateTarget))
  }

  async copyMessage(event) {
    const button = event.currentTarget
    const bubble = button
      .closest("[data-companion-message-row]")
      ?.querySelector('[data-role="assistant"]')
    if (!bubble) return
    const text =
      this._rawByBubble.get(bubble) ?? this.#bubbleTranscriptText(bubble)
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      return
    }
    const copy = button.querySelector("[data-companion-copy-label]")
    const copied = button.querySelector("[data-companion-copied-label]")
    // Announced from a status next to the button, not from inside it: not
    // every screen reader reads a live region nested in a <button>.
    const status = button.parentElement?.querySelector(
      "[data-companion-copy-status]",
    )
    copy?.classList.add("hidden")
    copied?.classList.remove("hidden")
    if (status) status.textContent = copied?.textContent?.trim() ?? ""
    clearTimeout(button._companionCopiedTimer)
    button._companionCopiedTimer = setTimeout(() => {
      copy?.classList.remove("hidden")
      copied?.classList.add("hidden")
      if (status) status.textContent = ""
    }, 1500)
  }

  // Regenerate is offered on the LAST answer only, and only once its turn is
  // over: it replaces that turn, so on an earlier answer it would rewrite
  // history the later turns were built on.
  #syncRegenerate() {
    if (!this.uxRefreshValue || !this.hasMessagesTarget) return
    const buttons = this.messagesTarget.querySelectorAll(
      "[data-companion-regenerate]",
    )
    buttons.forEach((button) => button.classList.add("hidden"))
    // A turn in flight — sent and not yet answered, or still answering — means
    // the last finished answer is no longer the last word.
    if (this.streamingMessageId || this._steps || this._turnRunning) return
    const answers = this.messagesTarget.querySelectorAll(
      '[data-role="assistant"]:not([data-state])',
    )
    const last = answers[answers.length - 1]
    const row = last?.closest("[data-companion-message-row]")
    if (!row || row !== this.#lastRowOf("assistant")) return
    if (!this.#previousUserRow(row)) return
    row.querySelector("[data-companion-regenerate]")?.classList.remove("hidden")
  }

  regenerateMessage(event) {
    if (this.streamingMessageId || this._steps || this._turnRunning) return
    const row = event.currentTarget.closest("[data-companion-message-row]")
    const userRow = row && this.#previousUserRow(row)
    if (!userRow) return
    const text = this.#bubbleTranscriptText(
      userRow.querySelector('[data-role="user"]'),
    )
    if (!text) return
    this.#removeTurnFrom(userRow)
    this.#sendUserMessage(text)
  }

  // A turn's error, in the transcript. Returns false when the template is not
  // mounted, so the caller falls back to the banner.
  #appendInlineError(message) {
    if (!this.hasInlineErrorTemplateTarget || !this.hasMessagesTarget)
      return false
    const el = this.#cloneTemplate(this.inlineErrorTemplateTarget)
    el.querySelector("[data-companion-inline-error-text]").textContent = message
    const retry = el.querySelector('[data-testid="ai-companion-inline-retry"]')
    const userRow = this.#lastRowOf("user")
    const text = userRow
      ? this.#bubbleTranscriptText(userRow.querySelector('[data-role="user"]'))
      : ""
    if (text) retry?.setAttribute("data-ai--companion-retry-text-param", text)
    else retry?.remove()
    this.messagesTarget.appendChild(el)
    this.#syncTranscriptState()
    this.scrollToBottom({ smooth: true })
    return true
  }

  // Retry from an inline error: the failed turn (its question, whatever it
  // managed to say, the error) leaves the transcript and the question is sent
  // again — the same reason handleRetryMessage drops the user row: replayed as
  // history AND sent as the message, the model would read it twice.
  retryFailedTurn(event) {
    const { retryText } = event.params
    const errorRow = event.currentTarget.closest(
      "[data-companion-inline-error]",
    )
    if (!retryText || !errorRow) return
    const userRow = this.#previousUserRow(errorRow)
    if (userRow) this.#removeTurnFrom(userRow)
    else errorRow.remove()
    this.#sendUserMessage(retryText)
  }

  #lastRowOf(role) {
    const bubbles = this.messagesTarget?.querySelectorAll(
      `[data-role="${role}"]`,
    )
    return bubbles?.[bubbles.length - 1]?.closest(
      "[data-companion-message-row]",
    )
  }

  #previousUserRow(row) {
    let sibling = row.previousElementSibling
    while (sibling) {
      if (sibling.querySelector?.('[data-role="user"]')) return sibling
      sibling = sibling.previousElementSibling
    }
    return null
  }

  // Removes `row` and everything after it: the turn it opens — its answer,
  // cards, steps line and error.
  #removeTurnFrom(row) {
    while (row.nextElementSibling) row.nextElementSibling.remove()
    row.remove()
    this.#syncTranscriptState()
  }

  #isNearBottom() {
    const el = this.messagesTarget
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight <= 160
  }

  // Any scroll UP unpins, however small (wheel, touch, keys, scrollbar):
  // content growing below fires no scroll event, and a programmatic scroll
  // only ever moves down. Only reaching the very bottom pins again — the
  // follow-along zone used to be 160px, so a wheel notch or two never left
  // it and every streamed chunk pulled the reader back down.
  #syncJumpToLatest() {
    const el = this.messagesTarget
    const top = el.scrollTop
    if (top < this._lastScrollTop - 1) {
      this._pinned = false
    } else if (el.scrollHeight - top - el.clientHeight <= 16) {
      this._pinned = true
      this.#hideJumpToLatest()
    }
    this._lastScrollTop = top
  }

  #showJumpToLatest() {
    if (this.hasJumpToLatestTarget)
      this.jumpToLatestTarget.classList.remove("hidden")
  }

  #hideJumpToLatest() {
    if (this.hasJumpToLatestTarget)
      this.jumpToLatestTarget.classList.add("hidden")
  }

  jumpToLatest() {
    this.scrollToBottom({ smooth: true, userInitiated: true })
  }

  // ──────────────────────────────────────────────────────────────────────
  // Playground-mode hooks (no-op when playgroundModeValue is false)

  forwardToInspector(data) {
    const inspectorEl = document.querySelector(
      '[data-controller~="ai--json-inspector"]',
    )
    if (!inspectorEl) return
    const inspector = this.application.getControllerForElementAndIdentifier(
      inspectorEl,
      "ai--json-inspector",
    )
    if (!inspector?.append) return
    // Fire-and-forget; the inspector's #append is async (dynamic shiki import).
    inspector.append(data).catch((err) => {
      console.warn("[ai--companion] inspector append failed:", err)
    })
  }

  trackLatency(data) {
    if (data.type === "thinking") {
      this.thinkingStartedAt = performance.now()
      return
    }
    if (data.type === "done" && this.thinkingStartedAt != null) {
      const ms = performance.now() - this.thinkingStartedAt
      this.thinkingStartedAt = null
      document.dispatchEvent(
        new CustomEvent("playground:latency-sample", { detail: { ms } }),
      )
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // DOM helpers

  appendTextBubble(role, content, id = null, { instant = false } = {}) {
    const bubble = this.createBubble(role, id, { instant })
    if (role === "assistant" && content) {
      // History replay — full message already complete, render markdown.
      bubble.innerHTML = renderMarkdown(content)
      bubble.classList.add(
        "prose",
        "prose-sm",
        "dark:prose-invert",
        "max-w-none",
      )
      this.#attachMessageActions(bubble, content)
    } else {
      // User bubbles, error rows, anything still streaming: textContent
      // keeps the DOM literal and safe from partial HTML. whitespace-pre-wrap
      // mirrors the React user bubble.
      bubble.textContent = content
      bubble.classList.add("whitespace-pre-wrap")
    }
    return bubble
  }

  // Build a message bubble mirroring the React CompanionMessage layout:
  // full-width row aligned left (assistant) / right (user), a max-w-[85%]
  // column, a sparkles avatar for assistant, and an asymmetric rounded
  // bubble (bg-muted/text-foreground for assistant, bg-primary/
  // text-primary-foreground for user). Returns the inner content bubble —
  // the element streaming + onDone + findStreamingBubble operate on, and
  // the one carrying data-role/data-message-id/aria for tests + a11y.
  createBubble(role, id, { instant = false } = {}) {
    const isAssistant = role === "assistant"

    const outer = document.createElement("div")
    outer.className = `flex w-full ${isAssistant ? "justify-start" : "justify-end"}`
    // New rows slide/fade in via the `#ai-companion-messages > *` rule in
    // application.css. History replays opt out (see onHistory).
    if (instant) outer.style.animation = "none"
    // Retry (workstream 3 sub-phase 2) needs to remove a whole failed/
    // interrupted turn and to walk backwards to find the triggering user
    // bubble — this marker is what `[data-companion-message-row]` targets.
    outer.dataset.companionMessageRow = "true"

    const column = document.createElement("div")
    // min-w-0 everywhere: flex items default to min-width:auto, so a wide
    // table inside the bubble would otherwise set the row's minimum width
    // and push the transcript past the panel edge (off-screen overflow).
    column.className = `flex min-w-0 max-w-[85%] flex-col gap-0.5 ${
      isAssistant ? "items-start" : "items-end"
    }`

    const row = document.createElement("div")
    row.className = `flex min-w-0 max-w-full items-start gap-2 ${isAssistant ? "" : "flex-row-reverse"}`

    if (isAssistant) {
      const avatar = document.createElement("div")
      avatar.className = "mt-1.5 shrink-0"
      avatar.dataset.companionAvatar = "true"
      if (this.hasAvatarTemplateTarget) {
        avatar.appendChild(this.#cloneTemplate(this.avatarTemplateTarget))
      }
      row.appendChild(avatar)
    }

    const bubble = document.createElement("div")
    bubble.className = isAssistant
      ? "min-w-0 max-w-full break-words rounded-xl rounded-bl-sm bg-muted text-foreground px-3 py-2 text-sm leading-relaxed"
      : "min-w-0 max-w-full break-words rounded-xl rounded-br-sm bg-primary text-primary-foreground px-3 py-2 text-sm leading-relaxed"
    bubble.dataset.role = role
    if (id) bubble.dataset.messageId = id
    // CONEK-3367 — Leaf-level aria for streaming assistant content. The
    // messages-list wrapper has its aria-live removed (sidebar_component.html.erb)
    // so the bubble itself is the live region. aria-busy="true" suppresses
    // per-chunk SR announcements; flipped to "false" in onDone() so the polite
    // region announces the completed message exactly once. See plan AC1.
    if (isAssistant) {
      bubble.setAttribute("role", "log")
      bubble.setAttribute("aria-live", "polite")
      bubble.setAttribute("aria-atomic", "false")
      bubble.setAttribute("aria-busy", "true")
    }

    row.appendChild(bubble)
    column.appendChild(row)
    outer.appendChild(column)
    this.messagesTarget.appendChild(outer)
    // The user's own line, and a replay, always land at the bottom; anything
    // else respects a user who scrolled up (ai.companion_ux_refresh).
    this.scrollToBottom({
      smooth: !instant,
      userInitiated: instant || !isAssistant,
    })
    this.#syncTranscriptState()
    return bubble
  }

  // Workstream 3 sub-phase 3 — toggles the empty state (vs. the transcript)
  // and the header's Clear button based on whether any bubble has actually
  // been rendered. Called from every path that adds/removes bubbles:
  // createBubble, onHistory, and the clear-confirm flow.
  //
  // The server-rendered transcript skeleton (TranscriptSkeletonComponent,
  // present when the shell mounts on an existing chat) counts as content for
  // the EMPTY STATE — that is its whole purpose, no flash of orb + chips
  // before `history` lands — but not as a message: there is nothing to Clear
  // yet.
  #syncTranscriptState() {
    const rows = this.hasMessagesTarget
      ? Array.from(this.messagesTarget.children)
      : []
    const isSkeleton = (el) => el.hasAttribute("data-companion-skeleton")
    const hasSkeleton = rows.some(isSkeleton)
    const hasMessages = rows.some((el) => !isSkeleton(el))
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle(
        "hidden",
        hasMessages || hasSkeleton,
      )
    }
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.toggle("hidden", !hasMessages)
    }
  }

  // The skeleton normally leaves with the first `history` frame (which wipes
  // the transcript). If the cable never delivers — rejected subscription
  // handled elsewhere, a proxy that drops the socket, a server that never
  // replays — the panel must not sit "busy" forever, so the placeholder is
  // dropped after the same budget a user message gets for its ack, and the
  // empty state takes over.
  #armSkeletonTimeout() {
    if (!this.#skeletonElement()) return
    this.#clearSkeletonTimeout()
    this._skeletonTimer = setTimeout(() => {
      this._skeletonTimer = null
      this.#skeletonElement()?.remove()
      this.#syncTranscriptState()
    }, this.constructor.ACK_TIMEOUT_MS)
  }

  #clearSkeletonTimeout() {
    if (!this._skeletonTimer) return
    clearTimeout(this._skeletonTimer)
    this._skeletonTimer = null
  }

  #skeletonElement() {
    if (!this.hasMessagesTarget) return null
    return this.messagesTarget.querySelector("[data-companion-skeleton]")
  }

  findStreamingBubble() {
    if (!this.streamingMessageId) return null
    return this.messagesTarget.querySelector(
      `[data-message-id="${cssEscape(this.streamingMessageId)}"]`,
    )
  }

  // `force: false` = follow-along scroll (streaming ticks): only stick to the
  // bottom when the user is already near it, so scrolling up to re-read
  // doesn't get yanked back down. `smooth` animates the jump for discrete
  // events (new bubble, card landing) unless the user prefers reduced motion.
  //
  // ai.companion_ux_refresh: a forced scroll no longer yanks a user who
  // scrolled up — new content below shows the "New messages" pill instead.
  // `userInitiated` (their own message, a replay, the pill itself) still goes.
  scrollToBottom({ force = true, smooth = false, userInitiated = false } = {}) {
    const el = this.messagesTarget
    if (!el) return
    const awayFromBottom = this.uxRefreshValue
      ? !this._pinned
      : !this.#isNearBottom()
    if (awayFromBottom && (!force || (this.uxRefreshValue && !userInitiated))) {
      if (this.uxRefreshValue) this.#showJumpToLatest()
      return
    }
    this.#hideJumpToLatest()
    this._pinned = true
    if (typeof el.scrollTo === "function") {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth && !prefersReducedMotion() ? "smooth" : "auto",
      })
    } else {
      el.scrollTop = el.scrollHeight
    }
  }

  setStatus(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    // Visible only when something is WRONG. This used to be
    // `toggle("hidden", !text)`, and since the cable's `connected` callback
    // calls setStatus(connectedStatusValue) on every connect, the happy path
    // rendered as a permanent full-width bordered bar reading "Connected"
    // under the header — a banner that tells the user nothing, on the one
    // surface where vertical space is scarcest. "Reconnecting…" is the only
    // state worth interrupting the layout for.
    //
    // textContent and data-state are still written in BOTH states, on purpose:
    // data-state is the scannable offline affordance from workstream 3.4, and
    // the i18n regression tests assert the text comes from the server-rendered
    // *-status-value attributes rather than a hardcoded literal.
    this.statusTarget.classList.toggle("hidden", !text || this.isConnected)
    // Workstream 3 sub-phase 3 — the pill reflects offline distinctly (not
    // just via its text), so it's scannable at a glance without reading it.
    this.statusTarget.setAttribute(
      "data-state",
      this.isConnected ? "connected" : "offline",
    )
    this.statusTarget.classList.toggle("text-destructive", !this.isConnected)
  }

  // What the indicator says while a tool runs. A user must never see the raw
  // tool name ("Calling list_drawings…" means nothing to them), so a mount
  // with the translated labels shows the mapped label or the generic one. Only
  // a mount without them — the admin Playground, a debugging surface — keeps
  // the name.
  #toolActivityLabel(toolName) {
    if (
      this.hasToolActivityLabelsValue &&
      this.toolActivityLabelsValue[toolName]
    ) {
      return this.toolActivityLabelsValue[toolName]
    }
    if (this.hasToolActivityFallbackValue) return this.toolActivityFallbackValue
    return toolName
  }

  setLoading(visible, text = "") {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.toggle("hidden", !visible)
    if (visible && text) {
      // Scoped to the label span — a bare querySelector("span") would grab
      // the thinking-dots wrapper and clobber the dots with the text.
      const label = this.loadingTarget.querySelector(
        "[data-companion-loading-label]",
      )
      if (label) label.textContent = text
    }
  }

  setError(message) {
    if (!this.hasErrorTarget) return
    // errorTarget also wraps the dismiss button (workstream 3 sub-phase 3)
    // — write into the nested text target, not the target itself, or a
    // future setError call would clobber the button along with the text.
    if (this.hasErrorTextTarget) this.errorTextTarget.textContent = message
    this.errorTarget.classList.toggle("hidden", !message)
  }

  // Manual dismiss (workstream 3 sub-phase 3) — the error banner previously
  // had no way to go away short of another error overwriting it.
  dismissError() {
    this.setError("")
  }
}

function cssEscape(value) {
  return CSS.escape(String(value))
}
